import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { TournamentStreamManager } from '../components/TournamentStreamManager';
import SportsVisioManager from '../components/SportsVisioManager';
import EventDivisionsManager from '../components/EventDivisionsManager';
import ProgramsManager from '../components/admin/ProgramsManager';
import LeadsManager from '../components/admin/LeadsManager';
import RostersManager from '../components/admin/RostersManager';
import SeedArticlesButton from '../components/admin/SeedArticlesButton';
import AnalyticsManager from '../components/admin/AnalyticsManager';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Shield,
  Users,
  DollarSign,
  Trophy,
  Video,
  BarChart3,
  UserCog,
  Trash2,
  Edit,
  Search,
  ChevronRight,
  Database,
  CreditCard,
  Radio,
  Play,
  Square,
  Copy,
  Eye,
  EyeOff,
  Link as LinkIcon,
  Plus,
  Settings,
  Youtube,
  Twitch,
  Monitor,
  Tv,
  MapPin,
  Calendar,
  Save,
  Loader2,
  CheckCircle2,
  XCircle,
  Upload,
  Download,
  FileSpreadsheet,
  RefreshCw,
  Scissors,
  Wifi,
  WifiOff,
  Key,
  Cloud,
  ArrowLeft,
  Film,
  Zap,
  Globe,
  Power,
  ExternalLink,
  X,
  ShieldCheck,
  ShieldOff,
  Ticket,
  Tag,
  ArrowUpCircle,
  Layers,
  Lock,
  Check,
  Mail,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Parse date without timezone shift
const safeDateStr = (dateStr) => {
  if (!dateStr) return 'No date';
  const s = String(dateStr);
  let d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y,m,dy] = s.split('-').map(Number);
    d = new Date(y, m-1, dy);
  } else {
    const dt = new Date(s);
    d = new Date(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate());
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const AdminDashboard = () => {
  const { user, token, isAdmin, isSuperAdmin, canAccessAdmin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [streams, setStreams] = useState([]);
  const [engineGames, setEngineGames] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStreamDialog, setShowStreamDialog] = useState(false);
  const [editingStream, setEditingStream] = useState(null);
  const [showStreamKey, setShowStreamKey] = useState({});
  const [multiCourtEvents, setMultiCourtEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventStreamConfigs, setEventStreamConfigs] = useState({});
  const [savingCourt, setSavingCourt] = useState({});
  const [showGameDialog, setShowGameDialog] = useState(false);
  const [showEditGameDialog, setShowEditGameDialog] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [showGameStreamDialog, setShowGameStreamDialog] = useState(false);
  const [gameStreamForm, setGameStreamForm] = useState({
    game_id: '',
    custom_rtmp_url: '',
    custom_stream_key: '',
    playback_url: '',
  });
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportEventId, setBulkImportEventId] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showEditEventDialog, setShowEditEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    name: '',
    sport: 'basketball',
    location: '',
    date: '',
    end_date: '',
    description: '',
    status: 'upcoming',
    is_featured: false,
  });
  const [allEvents, setAllEvents] = useState([]);
  const [manageEvent, setManageEvent] = useState(null);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [liveInputs, setLiveInputs] = useState([]);
  const [vodList, setVodList] = useState([]);
  const [clipsList, setClipsList] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(false);
  const [eventStreams, setEventStreams] = useState([]);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [upgradeUser, setUpgradeUser] = useState(null);
  const [upgradePlan, setUpgradePlan] = useState('plus_quarterly');
  const [expandedStreamEventId, setExpandedStreamEventId] = useState(null);
  
  // Preview Gate state
  const [previewGateEnabled, setPreviewGateEnabled] = useState(true);
  const [previewGateOpenUntil, setPreviewGateOpenUntil] = useState(null);
  const [previewGateDaysRemaining, setPreviewGateDaysRemaining] = useState(null);
  const [togglingGate, setTogglingGate] = useState(false);
  
  // VOD URL Management state
  const [showVodDialog, setShowVodDialog] = useState(false);
  const [vodForm, setVodForm] = useState({
    game_id: '',
    game_title: '',
    video_url: '',
    cloudflare_video_uid: '',
    thumbnail_url: '',
    duration_seconds: '',
  });
  const [savingVod, setSavingVod] = useState(false);

  // Roster management moved to <RostersManager /> component (P2 refactor 2026-02-22)

  // Add Member state
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [addMemberForm, setAddMemberForm] = useState({ name: '', email: '', role: 'athlete', phone: '', program: '', bypass_payment: false });
  const [addingMember, setAddingMember] = useState(false);

  // Discount codes state
  const [discountCodes, setDiscountCodes] = useState([]);
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [discountForm, setDiscountForm] = useState({ code: '', discount_percent: 100, max_uses: 0, description: '' });
  const [savingDiscount, setSavingDiscount] = useState(false);

  const fetchDiscountCodes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/discount-codes`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setDiscountCodes(await res.json());
    } catch {}
  };

  const handleCreateDiscount = async () => {
    if (!discountForm.code) { toast.error('Code is required'); return; }
    setSavingDiscount(true);
    try {
      const res = await fetch(`${API_URL}/api/discount-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(discountForm)
      });
      if (res.ok) {
        toast.success('Discount code created');
        setShowDiscountDialog(false);
        setDiscountForm({ code: '', discount_percent: 100, max_uses: 0, description: '' });
        fetchDiscountCodes();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create code');
      }
    } catch { toast.error('Error creating code'); }
    finally { setSavingDiscount(false); }
  };

  const toggleDiscount = async (codeId) => {
    try {
      const res = await fetch(`${API_URL}/api/discount-codes/${codeId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) fetchDiscountCodes();
    } catch {}
  };

  const deleteDiscount = async (codeId) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await fetch(`${API_URL}/api/discount-codes/${codeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchDiscountCodes();
    } catch {}
  };

  // Event Pass management state
  const [showPassDialog, setShowPassDialog] = useState(false);
  const [passEventId, setPassEventId] = useState(null);
  const [passEventName, setPassEventName] = useState('');
  const [passTiers, setPassTiers] = useState([]);
  const [passForm, setPassForm] = useState({ name: '', price: '', description: '', valid_days: '' });
  const [savingPass, setSavingPass] = useState(false);

  const fetchPassTiers = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/passes`);
      if (res.ok) {
        const data = await res.json();
        setPassTiers(data.tiers || []);
      }
    } catch {}
  };

  const openPassDialog = (event) => {
    setPassEventId(event.id);
    setPassEventName(event.name);
    setPassForm({ name: '', price: '', description: '', valid_days: '' });
    setShowPassDialog(true);
    fetchPassTiers(event.id);
  };

  const handleCreatePass = async () => {
    if (!passForm.name || !passForm.price) {
      toast.error('Name and price are required');
      return;
    }
    setSavingPass(true);
    try {
      const authToken = token || sessionStorage.getItem('rooam_token');
      const body = {
        name: passForm.name,
        price: parseFloat(passForm.price),
        description: passForm.description || null,
        valid_days: passForm.valid_days ? passForm.valid_days.split(',').map(d => d.trim()) : null,
      };
      const res = await fetch(`${API_URL}/api/events/${passEventId}/passes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        toast.success('Pass tier created');
        setPassForm({ name: '', price: '', description: '', valid_days: '' });
        fetchPassTiers(passEventId);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create pass');
      }
    } catch (err) {
      toast.error('Failed to create pass');
    } finally {
      setSavingPass(false);
    }
  };

  const handleDeletePass = async (passId) => {
    try {
      const authToken = token || sessionStorage.getItem('rooam_token');
      const res = await fetch(`${API_URL}/api/events/${passEventId}/passes/${passId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` },
      });
      if (res.ok) {
        toast.success('Pass tier deleted');
        fetchPassTiers(passEventId);
      } else {
        toast.error('Failed to delete pass');
      }
    } catch {
      toast.error('Failed to delete pass');
    }
  };

  const [gameForm, setGameForm] = useState({
    title: '',
    home_team_name: '',
    away_team_name: '',
    home_team_id: '',
    away_team_id: '',
    home_team_color: '#0066CC',
    away_team_color: '#CC0000',
    sport: 'basketball',
    division: '',
    location: '',
    venue_name: '',
    period_length_minutes: 12,
    periods_count: 4,
    event_id: '',
    scheduled_time: '',
    // Stream configuration
    custom_rtmp_url: '',
    custom_stream_key: '',
    playback_url: '',
  });
  const [gameEventDivisions, setGameEventDivisions] = useState([]);
  const [gameEventTeams, setGameEventTeams] = useState([]);
  const [streamForm, setStreamForm] = useState({
    stream_name: '',
    platform: 'rtmp',
    description: '',
    sport: 'Basketball',
    rtmp_url: '',
    stream_key: '',
    playback_url: '',
    youtube_video_id: '',
    twitch_channel: '',
    dvr_enabled: true,
    low_latency: false,
    auto_record: true,
    tournament_id: '',
    match_id: '',
    game_id: '',  // Link stream to engine game
  });

  // Auth check - only admin and operator roles can access
  useEffect(() => {
    if (!authLoading && !isAuthenticated()) {
      toast.error('Please login to access admin panel');
      navigate('/login');
      return;
    }
    if (!authLoading && isAuthenticated() && !canAccessAdmin()) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
  }, [authLoading, isAuthenticated, canAccessAdmin, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch admin stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats`);
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Fetch users
      const usersRes = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (usersRes.ok) {
        setUsers(await usersRes.json());
      }

      // Fetch transactions
      const transRes = await fetch(`${API_URL}/api/payments/transactions`);
      if (transRes.ok) {
        setTransactions(await transRes.json());
      }

      // Fetch streams
      const streamsRes = await fetch(`${API_URL}/api/admin/streams`);
      if (streamsRes.ok) {
        setStreams(await streamsRes.json());
      }

      // Fetch platforms
      const platformsRes = await fetch(`${API_URL}/api/admin/streams/platforms`);
      if (platformsRes.ok) {
        const data = await platformsRes.json();
        setPlatforms(data.platforms || []);
      }

      // Fetch tournaments for linking
      const tournamentsRes = await fetch(`${API_URL}/api/tournaments`);
      if (tournamentsRes.ok) {
        setTournaments(await tournamentsRes.json());
      }

      // Fetch multi-court events
      const eventsRes = await fetch(`${API_URL}/api/events?include_hidden=true`);
      if (eventsRes.ok) {
        let eventsData = await eventsRes.json();
        // Sort events by date - most recent first
        eventsData.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        setAllEvents(eventsData);
        // Filter for multi-court events
        const multiCourt = eventsData.filter(e => e.type === 'multi_court' || e.courts?.length > 0);
        setMultiCourtEvents(multiCourt);
      }

      // Fetch engine games (new game engine)
      const engineGamesRes = await fetch(`${API_URL}/api/engine/games?include_hidden=true`);
      if (engineGamesRes.ok) {
        let gamesData = await engineGamesRes.json();
        // Sort games by scheduled_time or created_at - most recent first
        gamesData.sort((a, b) => {
          const dateA = new Date(a.scheduled_time || a.created_at || 0);
          const dateB = new Date(b.scheduled_time || b.created_at || 0);
          return dateB - dateA;
        });
        setEngineGames(gamesData);
      }
      
      // Fetch event streams for overview
      fetchEventStreams();
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    fetchLiveInputs();
    fetchVODs();
    fetchClips();
    fetchPreviewGateStatus();
    fetchDiscountCodes();
  }, []);

  const fetchPreviewGateStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/preview-gate/status`);
      if (res.ok) {
        const data = await res.json();
        setPreviewGateEnabled(data.enabled);
        setPreviewGateOpenUntil(data.open_until || null);
        setPreviewGateDaysRemaining(data.days_remaining || null);
      }
    } catch (err) { console.error('Failed to fetch preview gate status:', err); }
  };

  const togglePreviewGate = async () => {
    setTogglingGate(true);
    try {
      const res = await fetch(`${API_URL}/api/preview-gate/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewGateEnabled(data.enabled);
        setPreviewGateOpenUntil(null);
        setPreviewGateDaysRemaining(null);
        toast.success(data.message);
      } else {
        toast.error('Failed to toggle preview gate');
      }
    } catch { toast.error('Error toggling preview gate'); }
    setTogglingGate(false);
  };

  const openPreviewGateForDays = async (days = 10) => {
    if (!window.confirm(`Open ALL games to the public for ${days} days?\n\nAuto-reverts on ${new Date(Date.now() + days * 86400000).toLocaleDateString()}.`)) return;
    setTogglingGate(true);
    try {
      const res = await fetch(`${API_URL}/api/preview-gate/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ days }),
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewGateEnabled(false);
        setPreviewGateOpenUntil(data.open_until);
        setPreviewGateDaysRemaining(days);
        toast.success(data.message);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to open public preview');
      }
    } catch { toast.error('Error opening public preview'); }
    setTogglingGate(false);
  };

  const fetchLiveInputs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cloudflare/live-inputs?limit=50`);
      if (res.ok) {
        const data = await res.json();
        let inputs = data.live_inputs || [];
        // Sort live inputs by created date - most recent first
        inputs.sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0));
        setLiveInputs(inputs);
      }
    } catch (err) {
      console.error('Error fetching live inputs:', err);
    }
  };

  const fetchVODs = async () => {
    setLoadingStreams(true);
    try {
      const res = await fetch(`${API_URL}/api/cloudflare/videos?limit=50`);
      if (res.ok) {
        const data = await res.json();
        let videos = data.videos || [];
        // Sort VODs by created date - most recent first
        videos.sort((a, b) => new Date(b.created || b.uploaded || 0) - new Date(a.created || a.uploaded || 0));
        setVodList(videos);
      }
    } catch (err) {
      console.error('Error fetching VODs:', err);
    }
    setLoadingStreams(false);
  };

  const fetchClips = async () => {
    try {
      const res = await fetch(`${API_URL}/api/vod/clips?limit=100`);
      if (res.ok) {
        let data = await res.json();
        // Sort clips by created date - most recent first
        data.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setClipsList(data);
      }
    } catch (err) {
      console.error('Error fetching clips:', err);
    }
  };

  // ===== Roster Management Functions =====
  // Moved to components/admin/RostersManager.jsx (P2 refactor 2026-02-22)
  // ===== End Roster Management =====

  const deleteClip = async (clipId) => {
    if (!window.confirm('Delete this clip?')) return;
    try {
      const res = await fetch(`${API_URL}/api/vod/clips/${clipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${user?.token || localStorage.getItem('token')}` }
      });
      if (res.ok) {
        toast.success('Clip deleted');
        fetchClips();
      } else {
        toast.error('Failed to delete clip');
      }
    } catch (err) {
      toast.error('Error deleting clip');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('User deleted successfully');
        setUsers(users.filter(u => u.id !== userId));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Error deleting user');
    }
  };

  const handleAddMember = async () => {
    if (!addMemberForm.name || !addMemberForm.email) {
      toast.error('Name and email are required');
      return;
    }
    setAddingMember(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(addMemberForm)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Member added successfully');
        setUsers([...users, { ...data.user, suspended: false }]);
        setShowAddMemberDialog(false);
        setAddMemberForm({ name: '', email: '', role: 'athlete', phone: '', program: '', bypass_payment: false });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add member');
      }
    } catch {
      toast.error('Error adding member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/role?role=${newRole}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Role updated to ${newRole}`);
        setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Error updating role');
    }
  };

  const handleSuspendUser = async (userId) => {
    const target = users.find(u => u.id === userId);
    const action = target?.suspended ? 'unsuspend' : 'suspend';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${userId}/suspend`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setUsers(users.map(u => u.id === userId ? { ...u, suspended: data.suspended } : u));
      } else {
        const error = await res.json();
        toast.error(error.detail || `Failed to ${action} user`);
      }
    } catch (error) {
      toast.error('Error updating user status');
    }
  };

  // Edit user handler
  const openEditUser = (userToEdit) => {
    setEditingUser({ ...userToEdit });
    setShowEditUserDialog(true);
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: editingUser.name,
          email: editingUser.email,
          role: editingUser.role
        })
      });
      
      if (res.ok) {
        toast.success('User updated successfully');
        setUsers(users.map(u => u.id === editingUser.id ? editingUser : u));
        setShowEditUserDialog(false);
        setEditingUser(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Error updating user');
    }
  };

  // Reset password handler
  const openResetPassword = (userToReset) => {
    setResetPasswordUser(userToReset);
    setNewPassword('');
    setShowResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${resetPasswordUser.id}/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ new_password: newPassword })
      });
      
      if (res.ok) {
        toast.success(`Password reset for ${resetPasswordUser.email}`);
        setShowResetPasswordDialog(false);
        setResetPasswordUser(null);
        setNewPassword('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Error resetting password');
    }
  };

  const handleUpgradeUser = async () => {
    if (!upgradeUser || !upgradePlan) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${upgradeUser.id}/upgrade`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ plan_id: upgradePlan })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setUpgradeUser(null);
        fetchUsers();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to upgrade user');
      }
    } catch {
      toast.error('Error upgrading user');
    }
  };

  // Fetch event streams for overview
  const fetchEventStreams = async () => {
    try {
      const eventsRes = await fetch(`${API_URL}/api/events`);
      if (!eventsRes.ok) return;
      const events = await eventsRes.json();
      
      const streamsData = await Promise.all(events.slice(0, 10).map(async (event) => {
        try {
          const configRes = await fetch(`${API_URL}/api/court-streams/event/${event.id}/config`);
          const config = configRes.ok ? await configRes.json() : { stream_mode: 'per_game' };
          
          if (config.stream_mode === 'per_court') {
            const courtsRes = await fetch(`${API_URL}/api/court-streams/event/${event.id}`);
            const courts = courtsRes.ok ? await courtsRes.json() : [];
            return { ...event, stream_config: config, court_streams: courts };
          }
          return { ...event, stream_config: config, court_streams: [] };
        } catch {
          return { ...event, stream_config: { stream_mode: 'per_game' }, court_streams: [] };
        }
      }));
      
      setEventStreams(streamsData);
    } catch (err) {
      console.error('Error fetching event streams:', err);
    }
  };

  // Edit game handler
  const openEditGame = (game) => {
    setEditingGame(game);
    setGameForm({
      title: game.title || '',
      home_team_name: game.home_team_name || '',
      away_team_name: game.away_team_name || '',
      sport: game.sport || 'basketball',
      division: game.division || '',
      location: game.location || '',
      venue_name: game.venue_name || '',
      period_length_minutes: 12,
      periods_count: 4,
      event_id: game.event_id || ''
    });
    setShowEditGameDialog(true);
  };

  const handleUpdateGame = async () => {
    if (!token) {
      toast.error('Please log in to update games');
      return;
    }
    
    if (!editingGame) {
      toast.error('No game selected for editing');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${editingGame.game_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: gameForm.title || `${gameForm.home_team_name} vs ${gameForm.away_team_name}`,
          home_team_name: gameForm.home_team_name,
          away_team_name: gameForm.away_team_name,
          sport: gameForm.sport,
          division: gameForm.division || null,
          location: gameForm.location,
          venue_name: gameForm.venue_name,
          event_id: gameForm.event_id || null
        })
      });
      
      if (res.ok) {
        toast.success('Game updated successfully');
        setShowEditGameDialog(false);
        setEditingGame(null);
        fetchData();
      } else {
        const err = await res.json();
        if (res.status === 401) {
          toast.error('Session expired. Please log in again.');
        } else {
          toast.error(err.detail || 'Failed to update game');
        }
      }
    } catch (err) {
      toast.error('Failed to update game');
    }
  };

  // Edit event handler
  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      name: event.name || '',
      sport: event.sport || 'basketball',
      location: event.location || '',
      date: event.date || '',
      end_date: event.end_date || '',
      description: event.description || '',
      is_featured: event.is_featured || false
    });
    setShowEditEventDialog(true);
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;
    
    try {
      const res = await fetch(`${API_URL}/api/events/${editingEvent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(eventForm)
      });
      
      if (res.ok) {
        toast.success('Event updated successfully');
        setShowEditEventDialog(false);
        setEditingEvent(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update event');
      }
    } catch (err) {
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId, eventName) => {
    if (!window.confirm(`Are you sure you want to delete "${eventName}"? Games will not be deleted.`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        toast.success('Event deleted');
        fetchData();
      } else {
        toast.error('Failed to delete event');
      }
    } catch (err) {
      toast.error('Failed to delete event');
    }
  };

  // Stream management handlers
  const handleCreateStream = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/admin/streams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(streamForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Stream created successfully!');
        setShowStreamDialog(false);
        resetStreamForm();
        fetchData();
        
        // Show the stream credentials
        if (data.stream?.stream_key) {
          toast.info(`Stream Key: ${data.stream.stream_key}`, { duration: 10000 });
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create stream');
      }
    } catch (error) {
      toast.error('Error creating stream');
    }
  };

  const handleUpdateStream = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/admin/streams/${editingStream.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(streamForm)
      });
      
      if (res.ok) {
        toast.success('Stream updated successfully!');
        setShowStreamDialog(false);
        setEditingStream(null);
        resetStreamForm();
        fetchData();
      } else {
        toast.error('Failed to update stream');
      }
    } catch (error) {
      toast.error('Error updating stream');
    }
  };

  const handleDeleteStream = async (streamId) => {
    if (!window.confirm('Are you sure you want to delete this stream?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/streams/${streamId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success('Stream deleted');
        setStreams(streams.filter(s => s.id !== streamId));
      } else {
        toast.error('Failed to delete stream');
      }
    } catch (error) {
      toast.error('Error deleting stream');
    }
  };

  const handleUpdateStreamStatus = async (streamId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/streams/${streamId}/status?status=${status}`, {
        method: 'PUT'
      });
      
      if (res.ok) {
        toast.success(`Stream status updated to ${status}`);
        setStreams(streams.map(s => s.id === streamId ? { ...s, status } : s));
      }
    } catch (error) {
      toast.error('Error updating stream status');
    }
  };

  const resetStreamForm = () => {
    setStreamForm({
      stream_name: '',
      platform: 'rtmp',
      description: '',
      sport: 'Basketball',
      rtmp_url: '',
      stream_key: '',
      playback_url: '',
      youtube_video_id: '',
      twitch_channel: '',
      dvr_enabled: true,
      low_latency: false,
      auto_record: true,
      tournament_id: '',
      match_id: '',
      game_id: '',
    });
  };

  const openEditStream = async (stream) => {
    // Fetch full stream details including stream key
    try {
      const res = await fetch(`${API_URL}/api/admin/streams/${stream.id}`);
      if (res.ok) {
        const fullStream = await res.json();
        setEditingStream(fullStream);
        setStreamForm({
          stream_name: fullStream.stream_name || '',
          platform: fullStream.platform || 'rtmp',
          description: fullStream.description || '',
          sport: fullStream.sport || 'Basketball',
          rtmp_url: fullStream.rtmp_url || '',
          stream_key: fullStream.stream_key || '',
          playback_url: fullStream.playback_url || '',
          youtube_video_id: fullStream.youtube_video_id || '',
          twitch_channel: fullStream.twitch_channel || '',
          dvr_enabled: fullStream.dvr_enabled ?? true,
          low_latency: fullStream.low_latency ?? false,
          auto_record: fullStream.auto_record ?? true,
          tournament_id: fullStream.tournament_id || '',
          match_id: fullStream.match_id || '',
          game_id: fullStream.game_id || '',
        });
        setShowStreamDialog(true);
      }
    } catch (error) {
      toast.error('Error loading stream details');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'youtube': return <Youtube className="h-4 w-4 text-red-500" />;
      case 'twitch': return <Twitch className="h-4 w-4 text-purple-500" />;
      case 'mux': return <Video className="h-4 w-4 text-pink-500" />;
      default: return <Monitor className="h-4 w-4 text-blue-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'live':
      case 'active':
        return <Badge className="bg-green-500/20 text-green-400 border-green-500"><Play className="h-3 w-3 mr-1" />Live</Badge>;
      case 'idle':
        return <Badge className="bg-gray-500/20 text-gray-400 border-gray-500"><Square className="h-3 w-3 mr-1" />Idle</Badge>;
      case 'ended':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500">Ended</Badge>;
      default:
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500">{status}</Badge>;
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'admin': return 'bg-red-500/20 text-red-400 border-red-500';
      case 'creator': return 'bg-purple-500/20 text-purple-400 border-purple-500';
      case 'coach': return 'bg-green-500/20 text-green-400 border-green-500';
      case 'scout': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500';
      default: return 'bg-rooam-blue/20 text-rooam-blue border-rooam-blue';
    }
  };

  // Multi-court event handlers
  const fetchEventDetails = async (eventId) => {
    try {
      const res = await fetch(`${API_URL}/api/events/multi-court/${eventId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedEvent(data);
        // Initialize stream configs from court data
        const configs = {};
        data.courts?.forEach(court => {
          configs[court.id] = { ...court.stream_config };
        });
        setEventStreamConfigs(configs);
      }
    } catch (error) {
      toast.error('Failed to load event details');
    }
  };

  const extractYouTubeVideoId = (input) => {
    if (!input) return null;
    if (/^[a-zA-Z0-9_-]{11}$/.test(input.trim())) return input.trim();
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/live\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = input.match(pattern);
      if (match) return match[1];
    }
    return input.trim();
  };

  const handleEventStreamConfigChange = (courtId, field, value) => {
    if (field === 'youtube_video_id') {
      // Warn if user enters RTMP URL instead of YouTube URL
      if (value && value.includes('rtmp://')) {
        toast.error('Please enter a YouTube video URL, not an RTMP URL. The RTMP URL is used in OBS, not here.');
        return;
      }
      value = extractYouTubeVideoId(value);
    }
    setEventStreamConfigs(prev => ({
      ...prev,
      [courtId]: { ...prev[courtId], [field]: value }
    }));
  };

  const saveEventStreamConfig = async (courtId, courtName) => {
    setSavingCourt(prev => ({ ...prev, [courtId]: true }));
    try {
      const config = eventStreamConfigs[courtId];
      const court = selectedEvent.courts.find(c => c.id === courtId);
      
      // Validate YouTube video ID
      if (config.youtube_video_id && config.youtube_video_id.includes('rtmp://')) {
        toast.error('Invalid YouTube Video ID. Please enter the YouTube live stream URL, not the RTMP ingest URL.');
        setSavingCourt(prev => ({ ...prev, [courtId]: false }));
        return;
      }
      
      const res = await fetch(`${API_URL}/api/events/multi-court/${selectedEvent.id}/courts/${courtId}/stream`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          court_id: courtId,
          court_name: courtName,
          sport: court?.sport || 'Basketball',
          platform: 'youtube',
          youtube_video_id: config.youtube_video_id || null,
          stream_key: config.stream_key || null,
          playback_url: config.youtube_video_id 
            ? `https://www.youtube.com/watch?v=${config.youtube_video_id}` 
            : null,
          status: config.status || 'offline'
        })
      });

      if (res.ok) {
        toast.success(`Stream config saved for ${courtName}`);
        fetchEventDetails(selectedEvent.id);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Error saving stream config');
    }
    setSavingCourt(prev => ({ ...prev, [courtId]: false }));
  };

  const getSportColor = (sport) => {
    switch (sport) {
      case 'Basketball': return { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: '🏀' };
      case 'Soccer': return { bg: 'bg-green-500/20', text: 'text-green-400', icon: '⚽' };
      case 'Flag Football': return { bg: 'bg-purple-500/20', text: 'text-purple-400', icon: '🏈' };
      default: return { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: '🎯' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-rooam-black flex items-center justify-center">
        <div className="text-white">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rooam-black">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-rooam-red" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">Admin Dashboard</h1>
            </div>
            <p className="text-sm sm:text-base text-gray-400">
              Full platform management and analytics
            </p>
          </div>
          <Badge className="bg-red-500/20 text-red-400 border-red-500 text-sm sm:text-lg px-3 sm:px-4 py-1 sm:py-2 w-fit">
            <Shield className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
            Administrator
          </Badge>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
          <Card className="bg-gradient-to-br from-rooam-blue/20 to-rooam-gray-medium border-rooam-blue/50 cursor-pointer hover:border-rooam-blue transition-all" onClick={() => navigate('/storage')}>
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <Database className="h-6 w-6 sm:h-8 sm:w-8 text-rooam-blue" />
              <div className="flex-1">
                <h3 className="font-semibold text-white text-sm sm:text-base">Game Storage</h3>
                <p className="text-xs text-gray-400 hidden sm:block">Manage recordings</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 hidden sm:block" />
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/20 to-rooam-gray-medium border-purple-500/50 cursor-pointer hover:border-purple-500 transition-all" onClick={() => setActiveTab('events')}>
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-purple-400" />
              <div className="flex-1">
                <h3 className="font-semibold text-white text-sm sm:text-base">Tournaments</h3>
                <p className="text-xs text-gray-400 hidden sm:block">Manage events & brackets</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 hidden sm:block" />
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500/20 to-rooam-gray-medium border-green-500/50 cursor-pointer hover:border-green-500 transition-all" onClick={() => setActiveTab('users')}>
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <Users className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
              <div className="flex-1">
                <h3 className="font-semibold text-white text-sm sm:text-base">Users & Teams</h3>
                <p className="text-xs text-gray-400 hidden sm:block">Manage accounts</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 hidden sm:block" />
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-yellow-500/20 to-rooam-gray-medium border-yellow-500/50 cursor-pointer hover:border-yellow-500 transition-all" onClick={() => setActiveTab('payments')}>
            <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <CreditCard className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400" />
              <div className="flex-1">
                <h3 className="font-semibold text-white text-sm sm:text-base">Payments</h3>
                <p className="text-xs text-gray-400 hidden sm:block">View transactions</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-400 hidden sm:block" />
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Mobile back-to-overview bar */}
          {activeTab !== 'overview' && (
            <div className="md:hidden flex items-center gap-2 mb-3 -mx-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-zinc-800 px-2"
                onClick={() => setActiveTab('overview')}
                data-testid="mobile-back-overview"
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Overview
              </Button>
              <span className="text-zinc-600">|</span>
              <span className="text-sm font-medium text-white capitalize">{activeTab === 'vods' ? 'VODs' : activeTab}</span>
            </div>
          )}

          {/* Desktop: horizontal tab bar */}
          <TabsList className="bg-rooam-gray-medium border border-rooam-gray-light mb-4 md:mb-6 w-full hidden md:flex overflow-x-auto scrollbar-hide">
            <TabsTrigger value="overview" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm">
              <BarChart3 className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Overview</span>
              <span className="sm:hidden">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="analytics-tab-trigger">
              <BarChart3 className="h-4 w-4 mr-1 sm:mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="games" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm">
              <Trophy className="h-4 w-4 mr-1 sm:mr-2" />
              Games
            </TabsTrigger>
            <TabsTrigger value="streams" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm">
              <Radio className="h-4 w-4 mr-1 sm:mr-2" />
              Streams
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm">
              <Tv className="h-4 w-4 mr-1 sm:mr-2" />
              Events
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm">
              <UserCog className="h-4 w-4 mr-1 sm:mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="rosters" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm">
              <Users className="h-4 w-4 mr-1 sm:mr-2" />
              Rosters
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm">
              <DollarSign className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Payments</span>
              <span className="sm:hidden">Pay</span>
            </TabsTrigger>
            {isSuperAdmin() && (
            <TabsTrigger value="discounts" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="discounts-tab">
              <Tag className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Discounts</span>
              <span className="sm:hidden">Disc</span>
            </TabsTrigger>
            )}
            <TabsTrigger value="vods" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm">
              <Video className="h-4 w-4 mr-1 sm:mr-2" />
              VODs
            </TabsTrigger>
            <TabsTrigger value="sportsvisio" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm">
              <Zap className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">AI Stats</span>
              <span className="sm:hidden">AI</span>
            </TabsTrigger>
            <TabsTrigger value="programs" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="programs-tab-trigger">
              <Globe className="h-4 w-4 mr-1 sm:mr-2" />
              Programs
            </TabsTrigger>
            <TabsTrigger value="leads" className="data-[state=active]:bg-rooam-red data-[state=active]:text-white text-xs sm:text-sm" data-testid="leads-tab-trigger">
              <Mail className="h-4 w-4 mr-1 sm:mr-2" />
              Leads
            </TabsTrigger>
          </TabsList>

          {/* Mobile: grid tab buttons */}
          <div className="md:hidden grid grid-cols-4 gap-2 mb-4">
            {[
              { value: 'overview', label: 'Stats', icon: BarChart3 },
              { value: 'analytics', label: 'Analytics', icon: BarChart3 },
              { value: 'games', label: 'Games', icon: Trophy },
              { value: 'streams', label: 'Streams', icon: Radio },
              { value: 'events', label: 'Events', icon: Tv },
              { value: 'users', label: 'Users', icon: UserCog },
              { value: 'rosters', label: 'Rosters', icon: Users },
              { value: 'payments', label: 'Pay', icon: DollarSign },
              ...(isSuperAdmin() ? [{ value: 'discounts', label: 'Disc', icon: Tag }] : []),
              { value: 'vods', label: 'VODs', icon: Video },
              { value: 'sportsvisio', label: 'AI Stats', icon: Zap },
              { value: 'programs', label: 'Programs', icon: Globe },
              { value: 'leads', label: 'Leads', icon: Mail },
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setActiveTab(value)}
                data-testid={`mobile-tab-${value}`}
                className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-lg text-[11px] font-medium transition-all ${
                  activeTab === value
                    ? 'bg-rooam-red text-white'
                    : 'bg-rooam-gray-medium text-zinc-400 border border-zinc-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Quick Action Buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <Link to="/setup-wizard">
                <Button className="bg-rooam-red hover:bg-red-600 text-white" data-testid="quick-start-wizard-btn">
                  <Zap className="h-4 w-4 mr-2" /> Quick Start Wizard
                </Button>
              </Link>
              {allEvents.length > 0 && (
                <Link to={`/game-day/${allEvents[0]?.id}`}>
                  <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500" data-testid="game-day-mode-btn">
                    <Radio className="h-4 w-4 mr-2" /> Game Day Mode
                  </Button>
                </Link>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-gray-400">Total Users</span>
                    <Users className="h-4 w-4 sm:h-5 sm:w-5 text-rooam-blue" />
                  </div>
                  <div className="text-xl sm:text-3xl font-bold text-white">{stats?.users_count || 0}</div>
                </CardContent>
              </Card>
              
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-gray-400">Teams</span>
                    <Users className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                  </div>
                  <div className="text-xl sm:text-3xl font-bold text-white">{stats?.teams_count || 0}</div>
                </CardContent>
              </Card>
              
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-gray-400">Tournaments</span>
                    <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
                  </div>
                  <div className="text-xl sm:text-3xl font-bold text-white">{stats?.tournaments_count || 0}</div>
                </CardContent>
              </Card>
              
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardContent className="p-3 sm:p-6">
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <span className="text-xs sm:text-sm text-gray-400">Revenue</span>
                    <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-rooam-blue" />
                  </div>
                  <div className="text-xl sm:text-3xl font-bold text-white">${stats?.total_revenue?.toFixed(0) || '0'}</div>
                </CardContent>
              </Card>
            </div>

            {/* Preview Gate Toggle */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light mb-6">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${previewGateEnabled ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                      {previewGateEnabled ? <Shield className="h-5 w-5 text-red-400" /> : <Eye className="h-5 w-5 text-green-400" />}
                    </div>
                    <div>
                      <h3 className="text-white text-sm font-semibold">
                        {previewGateEnabled ? 'Preview Gate' : 'Public Preview ACTIVE'}
                      </h3>
                      <p className="text-zinc-500 text-xs">
                        {previewGateEnabled
                          ? 'Non-subscribers see 60s preview, then paywall'
                          : previewGateDaysRemaining != null
                            ? `All viewers can watch full games. Auto-reverts in ${previewGateDaysRemaining} days (${previewGateOpenUntil ? new Date(previewGateOpenUntil).toLocaleDateString() : '—'}).`
                            : 'All viewers can watch full games without paywall (manual)'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {previewGateEnabled && (
                      <button
                        onClick={() => openPreviewGateForDays(10)}
                        disabled={togglingGate}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-md disabled:opacity-50"
                        data-testid="preview-gate-open-10d-btn"
                        title="Open all games to the public for 10 days, then auto-revert"
                      >
                        Open 10 Days
                      </button>
                    )}
                    <button
                      onClick={togglePreviewGate}
                      disabled={togglingGate}
                      className={`relative w-14 h-7 rounded-full transition-colors ${previewGateEnabled ? 'bg-red-600' : 'bg-zinc-600'}`}
                      data-testid="preview-gate-toggle"
                      title={previewGateEnabled ? 'Toggle gate off (manual)' : 'Re-enable paywall now'}
                    >
                      <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${previewGateEnabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>

                {!previewGateEnabled && previewGateDaysRemaining != null && (
                  <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-2 text-xs">
                    <Eye className="h-3.5 w-3.5 text-emerald-400" />
                    <span className="text-emerald-300">
                      Public preview is live. {previewGateDaysRemaining} day{previewGateDaysRemaining === 1 ? '' : 's'} remaining.
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Streams Overview - Like Creator Dashboard */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                    <Radio className="h-5 w-5 text-red-500" />
                    Event Stream Status
                  </CardTitle>
                  <Button size="sm" variant="outline" className="border-rooam-gray-light text-gray-300 text-xs" onClick={() => { fetchLiveInputs(); fetchEventStreams(); }}>
                    <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {eventStreams.length === 0 ? (
                  <div className="text-center py-6 text-zinc-500">
                    <Radio className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No events configured</p>
                    <p className="text-xs mt-1">Create events in the Events tab</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {eventStreams.map(event => (
                      <div key={event.id} className="bg-rooam-black rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="text-sm font-semibold text-white">{event.name}</div>
                            <div className="text-xs text-zinc-500">{event.sport} {event.date ? `| ${safeDateStr(event.date)}` : ''}</div>
                          </div>
                          <Badge className={event.stream_config?.stream_mode === 'per_court' ? 'bg-cyan-600 text-white text-xs' : 'bg-zinc-700 text-zinc-300 text-xs'}>
                            {event.stream_config?.stream_mode === 'per_court' ? 'Tournament' : 'Per Game'}
                          </Badge>
                        </div>
                        
                        {event.court_streams?.length > 0 ? (
                          <div className="space-y-1">
                            {event.court_streams.map(court => (
                              <div key={court.court_stream_id} className={`flex items-center justify-between p-2 rounded ${
                                court.is_live ? 'bg-emerald-900/20 border border-emerald-800/40' : 'bg-zinc-800/50'
                              }`}>
                                <div className="flex items-center gap-2">
                                  {court.is_live ? (
                                    <Wifi className="h-3.5 w-3.5 text-emerald-400" />
                                  ) : (
                                    <WifiOff className="h-3.5 w-3.5 text-zinc-600" />
                                  )}
                                  <span className="text-xs text-zinc-300">{court.court_name}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  {court.current_game_id && (
                                    <Badge className="bg-red-600 text-white text-[10px]">Game Active</Badge>
                                  )}
                                  <Badge className={court.is_live ? 'bg-emerald-600 text-white text-[10px]' : 'bg-zinc-700 text-zinc-400 text-[10px]'}>
                                    {court.is_live ? 'LIVE' : 'Offline'}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                            {event.court_streams.some(c => c.is_live) && (
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`take-offline-${event.id}`}
                                className="mt-2 w-full border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs"
                                onClick={async () => {
                                  if (!window.confirm(`Take all courts offline for "${event.name}"? This will stop showing them as LIVE.`)) return;
                                  try {
                                    const res = await fetch(`${API_URL}/api/court-streams/event/${event.id}/take-offline`, {
                                      method: 'POST',
                                      headers: { 'Authorization': `Bearer ${token}` }
                                    });
                                    if (res.ok) {
                                      const data = await res.json();
                                      toast.success(`${data.courts_updated} court(s) taken offline`);
                                      fetchEventStreams();
                                    } else {
                                      toast.error('Failed to take courts offline');
                                    }
                                  } catch {
                                    toast.error('Error taking courts offline');
                                  }
                                }}
                              >
                                <WifiOff className="h-3 w-3 mr-1" />
                                End Event — Take All Courts Offline
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="text-xs text-zinc-600 py-1">
                            {event.stream_config?.stream_mode === 'per_court' 
                              ? 'No courts configured' 
                              : 'Per-game streaming'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Cloudflare Live Inputs */}
                {liveInputs.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-zinc-700">
                    <div className="text-xs text-zinc-500 mb-2">Cloudflare Live Inputs</div>
                    <div className="space-y-1">
                      {liveInputs.slice(0, 5).map(input => (
                        <div key={input.uid} className={`flex items-center justify-between p-2 rounded ${
                          input.status === 'connected' ? 'bg-emerald-900/20 border border-emerald-800/40' : 'bg-rooam-black'
                        }`}>
                          <div className="flex items-center gap-2">
                            {input.status === 'connected' ? (
                              <Radio className="h-3 w-3 text-red-500 animate-pulse" />
                            ) : (
                              <div className="h-3 w-3 rounded-full bg-zinc-700" />
                            )}
                            <span className="text-xs text-zinc-300">{input.meta?.name || input.uid?.slice(0,12)}</span>
                          </div>
                          <Badge className={input.status === 'connected' ? 'bg-emerald-600 text-white text-[10px]' : 'bg-zinc-700 text-zinc-400 text-[10px]'}>
                            {input.status === 'connected' ? 'LIVE' : 'Offline'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Users by Role */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <CardTitle className="text-white text-base sm:text-lg">Users by Role</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-4">
                  {['athlete', 'coach', 'parent', 'scout', 'creator', 'admin'].map(role => (
                    <div key={role} className="bg-rooam-black p-2 sm:p-4 rounded-lg text-center">
                      <div className="text-lg sm:text-2xl font-bold text-white">
                        {stats?.users_by_role?.[role] || 0}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-400 capitalize">{role}s</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Super Admin: Data Management */}
            {isSuperAdmin() && (
              <Card className="bg-rooam-gray-medium border-red-500/30 border-2">
                <CardHeader>
                  <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-amber-400" />
                    Super Admin: Data Management
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <h4 className="text-red-400 font-semibold text-sm mb-1">Purge All Test Data</h4>
                      <p className="text-zinc-400 text-xs mb-3">
                        Removes all test users (keeps admin accounts), test games, events, discount codes, 
                        demo programs (Charlotte Elite, Metro Youth), athletes, and related data. This action cannot be undone.
                      </p>
                      <Button
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="purge-test-data-btn"
                        onClick={async () => {
                          if (!window.confirm('⚠️ DANGER: This will permanently delete ALL test data including users, games, events, programs, athletes, and discount codes. Only admin accounts (Lionel, David, Jill, Greg) will be kept.\n\nThis cannot be undone. Are you sure?')) return;
                          if (!window.confirm('Final confirmation: Purge all test data NOW?')) return;
                          try {
                            const res = await fetch(`${API_URL}/api/admin/purge-test-data`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              toast.success(`Data purged: ${data.results.users_deleted} users, ${data.results.games_deleted} games, ${data.results.events_deleted} events deleted`);
                              // Refresh data
                              window.location.reload();
                            } else {
                              const error = await res.json();
                              toast.error(error.detail || 'Failed to purge data');
                            }
                          } catch (err) {
                            toast.error('Failed to purge data');
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Purge All Test Data
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Games Tab - Engine Games Management */}
          <TabsContent value="games">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-rooam-red" />
                    Game Management
                  </CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Link to="/operator">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                        <Play className="h-4 w-4 mr-2" />
                        Operator Console
                      </Button>
                    </Link>
                    <Dialog open={showBulkImportDialog} onOpenChange={(open) => {
                      setShowBulkImportDialog(open);
                      if (!open) {
                        setBulkImportFile(null);
                        setBulkImportEventId('');
                        setBulkImportResult(null);
                      }
                    }}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="border-green-500 text-green-400 hover:bg-green-500/10" data-testid="bulk-import-btn">
                          <Upload className="h-4 w-4 mr-2" />
                          Bulk Import
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-white flex items-center gap-2">
                            <FileSpreadsheet className="h-5 w-5 text-green-400" />
                            Bulk Import Games
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          {/* Download Template */}
                          <div className="bg-rooam-black rounded-lg p-4 border border-rooam-gray-light">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="text-white font-medium text-sm">Download Template</h4>
                                <p className="text-zinc-500 text-xs mt-1">Get the CSV template with example data</p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-zinc-600 text-zinc-300"
                                onClick={() => window.open(`${API_URL}/api/engine/games/template/csv`, '_blank')}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                CSV
                              </Button>
                            </div>
                          </div>

                          {/* File Upload */}
                          <div>
                            <Label className="text-zinc-300">Upload Spreadsheet *</Label>
                            <div className="mt-2">
                              <Input
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={(e) => {
                                  setBulkImportFile(e.target.files?.[0] || null);
                                  setBulkImportResult(null);
                                }}
                                className="bg-rooam-gray-dark border-rooam-gray-light text-white file:bg-zinc-700 file:text-white file:border-0 file:mr-2"
                              />
                              <p className="text-zinc-500 text-xs mt-1">Supported: CSV, Excel (.xlsx, .xls)</p>
                            </div>
                          </div>

                          {/* Event Selection */}
                          <div>
                            <Label className="text-zinc-300">Assign to Event (optional)</Label>
                            <Select value={bulkImportEventId || "none"} onValueChange={(v) => setBulkImportEventId(v === "none" ? "" : v)}>
                              <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                                <SelectValue placeholder="Select an event..." />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                                <SelectItem value="none">No Event (Standalone Games)</SelectItem>
                                {allEvents.map(evt => (
                                  <SelectItem key={evt.id} value={evt.id}>
                                    {evt.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Import Result */}
                          {bulkImportResult && (
                            <div className={`rounded-lg p-4 border ${bulkImportResult.success ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                {bulkImportResult.success ? (
                                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                                ) : (
                                  <XCircle className="h-5 w-5 text-red-400" />
                                )}
                                <span className={`font-medium ${bulkImportResult.success ? 'text-green-400' : 'text-red-400'}`}>
                                  Import {bulkImportResult.success ? 'Complete' : 'Failed'}
                                </span>
                              </div>
                              <div className="text-sm text-zinc-300 space-y-1">
                                <p>✓ Imported: {bulkImportResult.imported} games</p>
                                {bulkImportResult.failed > 0 && (
                                  <p className="text-red-400">✗ Failed: {bulkImportResult.failed} rows</p>
                                )}
                                {bulkImportResult.errors?.length > 0 && (
                                  <div className="mt-2 text-xs text-red-400 max-h-32 overflow-y-auto">
                                    {bulkImportResult.errors.map((err, i) => (
                                      <p key={i}>{err}</p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Import Button */}
                          <Button
                            onClick={async () => {
                              if (!bulkImportFile) {
                                toast.error('Please select a file to import');
                                return;
                              }
                              setBulkImporting(true);
                              setBulkImportResult(null);
                              try {
                                const formData = new FormData();
                                formData.append('file', bulkImportFile);
                                if (bulkImportEventId) {
                                  formData.append('event_id', bulkImportEventId);
                                }
                                const res = await fetch(`${API_URL}/api/engine/games/bulk-import${bulkImportEventId ? `?event_id=${bulkImportEventId}` : ''}`, {
                                  method: 'POST',
                                  headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                                  body: formData
                                });
                                const data = await res.json();
                                if (res.ok) {
                                  setBulkImportResult(data);
                                  if (data.imported > 0) {
                                    toast.success(`Successfully imported ${data.imported} games!`);
                                    fetchData();
                                  }
                                } else {
                                  toast.error(data.detail || 'Import failed');
                                  setBulkImportResult({ success: false, imported: 0, failed: 1, errors: [data.detail || 'Unknown error'] });
                                }
                              } catch (err) {
                                toast.error('Failed to import games');
                                setBulkImportResult({ success: false, imported: 0, failed: 1, errors: [err.message] });
                              }
                              setBulkImporting(false);
                            }}
                            disabled={!bulkImportFile || bulkImporting}
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                          >
                            {bulkImporting ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Importing...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Import Games
                              </>
                            )}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Dialog open={showGameDialog} onOpenChange={setShowGameDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-rooam-red hover:bg-red-600 text-white" data-testid="create-game-btn">
                          <Plus className="h-4 w-4 mr-2" />
                          Create Game
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-white">Create New Game</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label className="text-zinc-300">Event *</Label>
                            <Select value={gameForm.event_id || "none"} onValueChange={(v) => {
                              const eid = v === "none" ? "" : v;
                              setGameForm({...gameForm, event_id: eid, division: '', home_team_name: '', away_team_name: '', home_team_id: '', away_team_id: '', home_team_color: '#0066CC', away_team_color: '#CC0000'});
                              // Load divisions for event
                              if (eid) {
                                fetch(`${API_URL}/api/events/${eid}/divisions`).then(r => r.json()).then(d => setGameEventDivisions(d)).catch(() => {});
                                fetch(`${API_URL}/api/events/${eid}/teams`).then(r => r.json()).then(t => setGameEventTeams(t)).catch(() => {});
                              } else {
                                setGameEventDivisions([]);
                                setGameEventTeams([]);
                              }
                            }}>
                              <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                                <SelectValue placeholder="Select an event..." />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                                <SelectItem value="none">No Event (Standalone Game)</SelectItem>
                                {allEvents.map(evt => (
                                  <SelectItem key={evt.id} value={evt.id}>
                                    {evt.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Division (from event structure or free text) */}
                          <div>
                            <Label className="text-zinc-300">Division</Label>
                            {gameEventDivisions.length > 0 ? (
                              <Select value={gameForm.division || "none"} onValueChange={(v) => setGameForm({...gameForm, division: v === "none" ? "" : v, home_team_name: '', away_team_name: '', home_team_id: '', away_team_id: ''})}>
                                <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                                  <SelectValue placeholder="Select division..." />
                                </SelectTrigger>
                                <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                                  <SelectItem value="none">No Division</SelectItem>
                                  {gameEventDivisions.map(div => (
                                    <SelectItem key={div.id} value={div.name}>
                                      {div.name} ({div.team_count || 0} teams)
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Input
                                value={gameForm.division}
                                onChange={(e) => setGameForm({...gameForm, division: e.target.value})}
                                placeholder="e.g. 17U Pool A, 16U Pool B"
                                className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                              />
                            )}
                          </div>

                          {/* Teams — dropdown from event teams or free text */}
                          {(() => {
                            const divTeams = gameEventTeams.filter(t => !gameForm.division || t.division_name === gameForm.division);
                            const useDropdown = divTeams.length > 0;
                            return (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <Label className="text-zinc-300">Home Team *</Label>
                                  {useDropdown ? (
                                    <Select value={gameForm.home_team_id || "custom"} onValueChange={(v) => {
                                      if (v === "custom") { setGameForm({...gameForm, home_team_id: '', home_team_name: '', home_team_color: '#0066CC'}); return; }
                                      const team = divTeams.find(t => t.id === v);
                                      if (team) setGameForm({...gameForm, home_team_id: team.id, home_team_name: team.name, home_team_color: team.color || '#0066CC'});
                                    }}>
                                      <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                                        <SelectValue placeholder="Select team..." />
                                      </SelectTrigger>
                                      <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                                        <SelectItem value="custom">Custom Team</SelectItem>
                                        {divTeams.filter(t => t.id !== gameForm.away_team_id).map(t => (
                                          <SelectItem key={t.id} value={t.id}>
                                            <div className="flex items-center gap-2">
                                              <div className="w-2 h-2 rounded-full" style={{backgroundColor: t.color || '#666'}} />
                                              {t.name}
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Input
                                      value={gameForm.home_team_name}
                                      onChange={(e) => setGameForm({...gameForm, home_team_name: e.target.value})}
                                      placeholder="Home Team"
                                      className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                                    />
                                  )}
                                </div>
                                <div>
                                  <Label className="text-zinc-300">Away Team *</Label>
                                  {useDropdown ? (
                                    <Select value={gameForm.away_team_id || "custom"} onValueChange={(v) => {
                                      if (v === "custom") { setGameForm({...gameForm, away_team_id: '', away_team_name: '', away_team_color: '#CC0000'}); return; }
                                      const team = divTeams.find(t => t.id === v);
                                      if (team) setGameForm({...gameForm, away_team_id: team.id, away_team_name: team.name, away_team_color: team.color || '#CC0000'});
                                    }}>
                                      <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                                        <SelectValue placeholder="Select team..." />
                                      </SelectTrigger>
                                      <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                                        <SelectItem value="custom">Custom Team</SelectItem>
                                        {divTeams.filter(t => t.id !== gameForm.home_team_id).map(t => (
                                          <SelectItem key={t.id} value={t.id}>
                                            <div className="flex items-center gap-2">
                                              <div className="w-2 h-2 rounded-full" style={{backgroundColor: t.color || '#666'}} />
                                              {t.name}
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Input
                                      value={gameForm.away_team_name}
                                      onChange={(e) => setGameForm({...gameForm, away_team_name: e.target.value})}
                                      placeholder="Away Team"
                                      className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          <div>
                            <Label className="text-zinc-300">Game Title (optional)</Label>
                            <Input
                              value={gameForm.title}
                              onChange={(e) => setGameForm({...gameForm, title: e.target.value})}
                              placeholder="e.g., Championship Finals"
                              className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-zinc-300">Sport</Label>
                              <Select value={gameForm.sport} onValueChange={(v) => setGameForm({...gameForm, sport: v})}>
                                <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                                  <SelectItem value="basketball">Basketball</SelectItem>
                                  <SelectItem value="soccer">Soccer</SelectItem>
                                  <SelectItem value="volleyball">Volleyball</SelectItem>
                                  <SelectItem value="football">Football</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-zinc-300">Location</Label>
                              <Input
                                value={gameForm.location}
                                onChange={(e) => setGameForm({...gameForm, location: e.target.value})}
                                placeholder="Venue/Court"
                                className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                              />
                            </div>
                          </div>
                          
                          {/* Scheduled Time */}
                          <div>
                            <Label className="text-zinc-300">Scheduled Time</Label>
                            <Input
                              type="datetime-local"
                              value={gameForm.scheduled_time}
                              onChange={(e) => setGameForm({...gameForm, scheduled_time: e.target.value})}
                              className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                            />
                            <p className="text-xs text-zinc-500 mt-1">When does this game start?</p>
                          </div>
                          
                          {/* Stream Configuration Section */}
                          <div className="border-t border-zinc-700 pt-4 mt-2">
                            <h4 className="text-sm font-semibold text-cyan-400 mb-3 flex items-center gap-2">
                              📺 Stream Configuration (Optional)
                            </h4>
                            <div className="space-y-3">
                              {/* Platform Quick Select - Cloudflare and Custom first */}
                              <div className="grid grid-cols-4 gap-2">
                                <button
                                  type="button"
                                  onClick={() => setGameForm({...gameForm, custom_rtmp_url: 'rtmps://live.cloudflare.com:443/live/'})}
                                  className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${gameForm.custom_rtmp_url?.includes('cloudflare') ? 'bg-orange-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                                >
                                  Cloudflare
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setGameForm({...gameForm, custom_rtmp_url: 'rtmp://'})}
                                  className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${gameForm.custom_rtmp_url && !gameForm.custom_rtmp_url.includes('cloudflare') && !gameForm.custom_rtmp_url.includes('youtube') && !gameForm.custom_rtmp_url.includes('twitch') ? 'bg-cyan-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                                >
                                  Custom
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setGameForm({...gameForm, custom_rtmp_url: 'rtmp://a.rtmp.youtube.com/live2/'})}
                                  className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${gameForm.custom_rtmp_url?.includes('youtube') ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                                >
                                  YouTube
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setGameForm({...gameForm, custom_rtmp_url: 'rtmp://live.twitch.tv/app/'})}
                                  className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${gameForm.custom_rtmp_url?.includes('twitch') ? 'bg-purple-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                                >
                                  Twitch
                                </button>
                              </div>
                              
                              <div>
                                <Label className="text-zinc-400 text-xs">RTMP Server URL</Label>
                                <Input
                                  value={gameForm.custom_rtmp_url}
                                  onChange={(e) => setGameForm({...gameForm, custom_rtmp_url: e.target.value})}
                                  placeholder="rtmps://live.cloudflare.com:443/live/"
                                  className="bg-rooam-gray-dark border-rooam-gray-light text-white text-sm h-9"
                                />
                              </div>
                              <div>
                                <Label className="text-zinc-400 text-xs">Stream Key</Label>
                                <Input
                                  value={gameForm.custom_stream_key}
                                  onChange={(e) => setGameForm({...gameForm, custom_stream_key: e.target.value})}
                                  placeholder="your-stream-key"
                                  className="bg-rooam-gray-dark border-rooam-gray-light text-white text-sm h-9"
                                />
                              </div>
                              <div>
                                <Label className="text-zinc-400 text-xs">Playback URL (HLS/MP4) - What viewers watch</Label>
                                <Input
                                  value={gameForm.playback_url}
                                  onChange={(e) => setGameForm({...gameForm, playback_url: e.target.value})}
                                  placeholder="https://customer-xxx.cloudflarestream.com/.../video.m3u8"
                                  className="bg-rooam-gray-dark border-rooam-gray-light text-white text-sm h-9"
                                />
                              </div>
                              <p className="text-xs text-zinc-500">
                                Leave blank to auto-generate Cloudflare stream. Or configure your own streaming source.
                              </p>
                            </div>
                          </div>
                          
                          <Button
                            onClick={async () => {
                              if (!gameForm.home_team_name || !gameForm.away_team_name) {
                                toast.error('Please enter both team names');
                                return;
                              }
                              try {
                                const payload = {...gameForm};
                                if (!payload.event_id) delete payload.event_id;
                                // Clean up empty stream fields
                                if (!payload.custom_rtmp_url) delete payload.custom_rtmp_url;
                                if (!payload.custom_stream_key) delete payload.custom_stream_key;
                                if (!payload.playback_url) delete payload.playback_url;
                                
                                const res = await fetch(`${API_URL}/api/engine/games`, {
                                  method: 'POST',
                                  headers: { 
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                  },
                                  body: JSON.stringify(payload)
                                });
                                if (res.ok) {
                                  toast.success('Game created successfully!');
                                  setShowGameDialog(false);
                                  setGameForm({ title: '', home_team_name: '', away_team_name: '', sport: 'basketball', division: '', location: '', venue_name: '', period_length_minutes: 12, periods_count: 4, event_id: '', scheduled_time: '', custom_rtmp_url: '', custom_stream_key: '', playback_url: '' });
                                  fetchData();
                                } else {
                                  const err = await res.json();
                                  toast.error(err.detail || 'Failed to create game');
                                }
                              } catch (err) {
                                toast.error('Failed to create game');
                              }
                            }}
                            className="w-full bg-rooam-red hover:bg-red-600"
                          >
                            Create Game
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input
                    placeholder="Search games..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-rooam-gray-dark border-rooam-gray-light text-white max-w-sm"
                  />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-rooam-gray-light">
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Game</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Event</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Sport</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Status</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Score</th>
                        <th className="text-left py-3 px-2 text-zinc-400 font-medium">Location</th>
                        <th className="text-right py-3 px-2 text-zinc-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {engineGames
                        .filter(g => {
                          if (!searchTerm) return true;
                          const s = searchTerm.toLowerCase();
                          return (g.title || '').toLowerCase().includes(s) ||
                                 (g.home_team_name || '').toLowerCase().includes(s) ||
                                 (g.away_team_name || '').toLowerCase().includes(s);
                        })
                        .map(game => {
                          const eventName = allEvents.find(e => e.id === game.event_id)?.name;
                          return (
                        <tr key={game.game_id} className={`border-b border-rooam-gray-light/50 hover:bg-rooam-gray-dark/50 ${game.hidden ? 'opacity-50' : ''}`}>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-white">{game.title || `${game.home_team_name} vs ${game.away_team_name}`}</div>
                              {game.hidden && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-bold uppercase">Hidden</span>}
                            </div>
                            <div className="text-xs text-zinc-500">{game.home_team_name} vs {game.away_team_name}{game.division ? ` • ${game.division}` : ''}</div>
                          </td>
                          <td className="py-3 px-2">
                            {eventName ? (
                              <Link to={`/event/${game.event_id}`} className="text-rooam-blue hover:underline text-xs">
                                {eventName}
                              </Link>
                            ) : (
                              <span className="text-zinc-600 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <Badge variant="outline" className="text-zinc-300 border-zinc-600 capitalize">
                              {game.sport || 'Basketball'}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            <Badge className={
                              game.status === 'live' ? 'bg-red-500' :
                              game.status === 'final' ? 'bg-zinc-600' :
                              game.status === 'halftime' ? 'bg-amber-500' :
                              'bg-blue-500'
                            }>
                              {game.status || 'scheduled'}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-white font-mono">
                            {game.score?.home || 0} - {game.score?.away || 0}
                          </td>
                          <td className="py-3 px-2 text-zinc-400">{game.location || '-'}</td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                                onClick={() => openEditGame(game)}
                                data-testid={`edit-game-${game.game_id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30"
                                title="Edit Stream Settings"
                                onClick={async () => {
                                  // Fetch current stream session for this game
                                  try {
                                    const res = await fetch(`${API_URL}/api/engine/stream/sessions/${game.game_id}`);
                                    if (res.ok) {
                                      const data = await res.json();
                                      const session = data.session || {};
                                      setGameStreamForm({
                                        game_id: game.game_id,
                                        game_title: game.title || `${game.home_team_name} vs ${game.away_team_name}`,
                                        custom_rtmp_url: session.custom_rtmp_url || session.rtmp_url || '',
                                        custom_stream_key: session.custom_stream_key || session.stream_key || '',
                                        playback_url: session.playback_url || game.video_url || '',
                                      });
                                    } else {
                                      // No stream session, start fresh
                                      setGameStreamForm({
                                        game_id: game.game_id,
                                        game_title: game.title || `${game.home_team_name} vs ${game.away_team_name}`,
                                        custom_rtmp_url: 'rtmps://live.cloudflare.com:443/live/',
                                        custom_stream_key: '',
                                        playback_url: game.video_url || '',
                                      });
                                    }
                                  } catch (e) {
                                    setGameStreamForm({
                                      game_id: game.game_id,
                                      game_title: game.title || `${game.home_team_name} vs ${game.away_team_name}`,
                                      custom_rtmp_url: 'rtmps://live.cloudflare.com:443/live/',
                                      custom_stream_key: '',
                                      playback_url: game.video_url || '',
                                    });
                                  }
                                  setShowGameStreamDialog(true);
                                }}
                              >
                                <Video className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                                title="Set VOD URL"
                                onClick={() => {
                                  setVodForm({
                                    game_id: game.game_id,
                                    game_title: game.title || `${game.home_team_name} vs ${game.away_team_name}`,
                                    video_url: game.video_url || '',
                                    cloudflare_video_uid: '',
                                    thumbnail_url: game.thumbnail_url || '',
                                    duration_seconds: '',
                                  });
                                  setShowVodDialog(true);
                                }}
                                data-testid={`set-vod-${game.game_id}`}
                              >
                                <Film className="h-4 w-4" />
                              </Button>
                              <Link to={`/operator/game/${game.game_id}`}>
                                <Button size="sm" variant="outline" className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                                  <Play className="h-4 w-4" />
                                </Button>
                              </Link>
                              <Link to={`/game-live/${game.game_id}`}>
                                <Button size="sm" variant="outline" className="border-zinc-600 text-zinc-300 hover:bg-zinc-700">
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </Link>
                              <Button
                                size="sm"
                                variant="outline"
                                className={game.hidden ? "border-green-600 text-green-400 hover:bg-green-900/30" : "border-yellow-600 text-yellow-400 hover:bg-yellow-900/30"}
                                title={game.hidden ? 'Show game (make visible)' : 'Hide game (from public)'}
                                onClick={async () => {
                                  try {
                                    const authToken = token || sessionStorage.getItem('rooam_token') || localStorage.getItem('rooam_token');
                                    const res = await fetch(`${API_URL}/api/engine/games/${game.game_id}/visibility`, {
                                      method: 'PUT',
                                      headers: { 'Authorization': `Bearer ${authToken}` }
                                    });
                                    if (res.ok) {
                                      const data = await res.json();
                                      toast.success(data.message);
                                      setEngineGames(engineGames.map(g => g.game_id === game.game_id ? { ...g, hidden: data.hidden } : g));
                                    } else {
                                      toast.error('Failed to toggle visibility');
                                    }
                                  } catch (err) {
                                    toast.error('Failed to toggle visibility');
                                  }
                                }}
                                data-testid={`toggle-visibility-game-${game.game_id}`}
                              >
                                {game.hidden ? <EyeOff className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={async () => {
                                  if (!window.confirm(`Delete game "${game.title || game.home_team_name + ' vs ' + game.away_team_name}"?`)) return;
                                  try {
                                    const authToken = token || sessionStorage.getItem('rooam_token') || localStorage.getItem('rooam_token');
                                    if (!authToken) {
                                      toast.error('Please log in to delete games');
                                      return;
                                    }
                                    const res = await fetch(`${API_URL}/api/engine/games/${game.game_id}`, { 
                                      method: 'DELETE',
                                      headers: { 'Authorization': `Bearer ${authToken}` }
                                    });
                                    if (res.ok) {
                                      toast.success('Game deleted');
                                      setEngineGames(engineGames.filter(g => g.game_id !== game.game_id));
                                    } else {
                                      const error = await res.json().catch(() => ({ detail: 'Unknown error' }));
                                      toast.error(error.detail || 'Failed to delete game');
                                    }
                                  } catch (err) {
                                    toast.error('Failed to delete game');
                                    console.error('Delete error:', err);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                      })}
                      {engineGames.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-zinc-500">
                            No games found. Create a game to get started.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Streams Tab */}
          <TabsContent value="streams">
            {/* Event Stream Configuration Section */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Tv className="h-5 w-5 text-cyan-400" />
                    Event Stream Configuration
                  </CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-rooam-gray-light text-gray-300" 
                    onClick={fetchEventStreams}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {eventStreams.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="h-10 w-10 text-gray-500 mx-auto mb-3" />
                    <p className="text-gray-400 text-sm">No events configured</p>
                    <p className="text-gray-500 text-xs mt-1">Create events in the Events tab to configure streams</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {eventStreams.map(event => (
                      <div key={event.id} className="bg-rooam-black rounded-lg p-4 border border-rooam-gray-light">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-white">{event.name}</h4>
                              <Badge className={event.stream_config?.stream_mode === 'per_court' ? 'bg-cyan-600 text-white' : 'bg-zinc-700 text-zinc-300'}>
                                {event.stream_config?.stream_mode === 'per_court' ? 'Tournament Mode' : 'Per Game Mode'}
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-400">
                              {event.sport} | {safeDateStr(event.date)}
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-2">
                            <Select 
                              value={event.stream_config?.stream_mode || 'per_game'}
                              onValueChange={async (mode) => {
                                try {
                                  const res = await fetch(`${API_URL}/api/court-streams/event/${event.id}/config`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ stream_mode: mode })
                                  });
                                  if (res.ok) {
                                    toast.success(`Stream mode updated to ${mode === 'per_court' ? 'Tournament' : 'Per Game'}`);
                                    fetchEventStreams();
                                  }
                                } catch (err) {
                                  toast.error('Failed to update stream mode');
                                }
                              }}
                            >
                              <SelectTrigger className="w-40 bg-rooam-gray-medium border-rooam-gray-light text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                <SelectItem value="per_game" className="text-white">Per Game</SelectItem>
                                <SelectItem value="per_court" className="text-white">Tournament (Per Court)</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                              onClick={() => setExpandedStreamEventId(expandedStreamEventId === event.id ? null : event.id)}
                              data-testid={`courts-toggle-${event.id}`}
                            >
                              <Settings className="h-4 w-4 mr-1" />
                              Courts
                            </Button>
                          </div>
                        </div>
                        
                        {/* Court management - shown when expanded via Courts button */}
                        {expandedStreamEventId === event.id && (
                          <div className="mt-4 pt-4 border-t border-rooam-gray-light">
                            <TournamentStreamManager 
                              eventId={event.id}
                              eventName={event.name}
                              token={token || localStorage.getItem('rooam_token')}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cloudflare Live Inputs */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Cloud className="h-5 w-5 text-orange-400" />
                    Cloudflare Live Inputs
                  </CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-rooam-gray-light text-gray-300" 
                    onClick={fetchLiveInputs}
                  >
                    <RefreshCw className="h-3 w-3 mr-1" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {liveInputs.length === 0 ? (
                  <div className="text-center py-6">
                    <Cloud className="h-10 w-10 text-gray-500 mx-auto mb-3 opacity-50" />
                    <p className="text-gray-400 text-sm">No Cloudflare live inputs found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {liveInputs.map(input => (
                      <div 
                        key={input.uid} 
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          input.status === 'connected' ? 'bg-emerald-900/20 border border-emerald-700/40' : 'bg-rooam-black'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {input.status === 'connected' ? (
                            <Radio className="h-4 w-4 text-red-500 animate-pulse" />
                          ) : (
                            <div className="h-4 w-4 rounded-full bg-zinc-700" />
                          )}
                          <div>
                            <div className="text-sm text-white font-medium">{input.meta?.name || 'Unnamed Input'}</div>
                            <div className="text-xs text-zinc-500 font-mono">{input.uid?.slice(0,16)}...</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={input.status === 'connected' ? 'bg-emerald-600 text-white' : 'bg-zinc-700 text-zinc-400'}>
                            {input.status === 'connected' ? 'LIVE' : 'Offline'}
                          </Badge>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-gray-400 hover:text-white h-8"
                            onClick={() => copyToClipboard(input.uid, 'Input ID')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="text-xs text-zinc-600 pt-2">
                      {liveInputs.filter(i => i.status === 'connected').length} live / {liveInputs.length} total inputs
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Manual Stream Configuration */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Radio className="h-5 w-5 text-rooam-red" />
                    Stream Configuration
                  </CardTitle>
                  <Dialog open={showStreamDialog} onOpenChange={(open) => {
                    setShowStreamDialog(open);
                    if (!open) {
                      setEditingStream(null);
                      resetStreamForm();
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-rooam-red hover:bg-red-600 text-white" data-testid="add-stream-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Stream
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-white">
                          {editingStream ? 'Edit Stream Configuration' : 'Create New Stream'}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={editingStream ? handleUpdateStream : handleCreateStream} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-300">Stream Name *</Label>
                            <Input
                              value={streamForm.stream_name}
                              onChange={(e) => setStreamForm({...streamForm, stream_name: e.target.value})}
                              placeholder="Game 1 - Team A vs Team B"
                              className="bg-rooam-black border-rooam-gray-light text-white"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-300">Platform *</Label>
                            <Select value={streamForm.platform} onValueChange={(v) => {
                              const platform = platforms.find(p => p.id === v);
                              setStreamForm({
                                ...streamForm, 
                                platform: v,
                                rtmp_url: platform?.default_rtmp || ''
                              });
                            }}>
                              <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                {platforms.map(p => (
                                  <SelectItem key={p.id} value={p.id} className="text-white hover:bg-rooam-gray-light">
                                    {p.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-gray-300">Description</Label>
                          <Textarea
                            value={streamForm.description}
                            onChange={(e) => setStreamForm({...streamForm, description: e.target.value})}
                            placeholder="Stream description..."
                            className="bg-rooam-black border-rooam-gray-light text-white"
                            rows={2}
                          />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-300">RTMP URL</Label>
                            <Input
                              value={streamForm.rtmp_url}
                              onChange={(e) => setStreamForm({...streamForm, rtmp_url: e.target.value})}
                              placeholder="rtmp://..."
                              className="bg-rooam-black border-rooam-gray-light text-white font-mono text-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-300">Stream Key</Label>
                            <Input
                              value={streamForm.stream_key}
                              onChange={(e) => setStreamForm({...streamForm, stream_key: e.target.value})}
                              placeholder="Auto-generated if empty"
                              className="bg-rooam-black border-rooam-gray-light text-white font-mono text-sm"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-gray-300">Playback URL (for viewers)</Label>
                          <Input
                            value={streamForm.playback_url}
                            onChange={(e) => setStreamForm({...streamForm, playback_url: e.target.value})}
                            placeholder="https://..."
                            className="bg-rooam-black border-rooam-gray-light text-white"
                          />
                        </div>

                        {streamForm.platform === 'youtube' && (
                          <div className="space-y-2">
                            <Label className="text-gray-300">YouTube Video ID</Label>
                            <Input
                              value={streamForm.youtube_video_id}
                              onChange={(e) => setStreamForm({...streamForm, youtube_video_id: e.target.value})}
                              placeholder="dQw4w9WgXcQ"
                              className="bg-rooam-black border-rooam-gray-light text-white"
                            />
                          </div>
                        )}

                        {streamForm.platform === 'twitch' && (
                          <div className="space-y-2">
                            <Label className="text-gray-300">Twitch Channel</Label>
                            <Input
                              value={streamForm.twitch_channel}
                              onChange={(e) => setStreamForm({...streamForm, twitch_channel: e.target.value})}
                              placeholder="channel_name"
                              className="bg-rooam-black border-rooam-gray-light text-white"
                            />
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-300">Link to Tournament</Label>
                            <Select value={streamForm.tournament_id || "none"} onValueChange={(v) => setStreamForm({...streamForm, tournament_id: v === "none" ? "" : v, match_id: ''})}>
                              <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                                <SelectValue placeholder="Select tournament" />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light max-h-60">
                                <SelectItem value="none" className="text-gray-400">None</SelectItem>
                                {tournaments.map(t => (
                                  <SelectItem key={t.id} value={t.id} className="text-white hover:bg-rooam-gray-light">
                                    {t.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-300">Link to Game (for Operator Console sync)</Label>
                            <Select value={streamForm.game_id || "none"} onValueChange={(v) => setStreamForm({...streamForm, game_id: v === "none" ? "" : v})}>
                              <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                                <SelectValue placeholder="Select game" />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light max-h-60">
                                <SelectItem value="none" className="text-gray-400">None</SelectItem>
                                {engineGames.map(g => (
                                  <SelectItem key={g.game_id} value={g.game_id} className="text-white hover:bg-rooam-gray-light">
                                    {g.home_team_name} vs {g.away_team_name} {g.status ? `(${g.status})` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-300">Sport</Label>
                            <Select value={streamForm.sport} onValueChange={(v) => setStreamForm({...streamForm, sport: v})}>
                              <SelectTrigger className="bg-rooam-black border-rooam-gray-light text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                <SelectItem value="Basketball" className="text-white">Basketball</SelectItem>
                                <SelectItem value="Soccer" className="text-white">Soccer</SelectItem>
                                <SelectItem value="Football" className="text-white">Football</SelectItem>
                                <SelectItem value="Volleyball" className="text-white">Volleyball</SelectItem>
                                <SelectItem value="Other" className="text-white">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 text-gray-300">
                            <input
                              type="checkbox"
                              checked={streamForm.dvr_enabled}
                              onChange={(e) => setStreamForm({...streamForm, dvr_enabled: e.target.checked})}
                              className="rounded border-rooam-gray-light"
                            />
                            DVR Enabled
                          </label>
                          <label className="flex items-center gap-2 text-gray-300">
                            <input
                              type="checkbox"
                              checked={streamForm.low_latency}
                              onChange={(e) => setStreamForm({...streamForm, low_latency: e.target.checked})}
                              className="rounded border-rooam-gray-light"
                            />
                            Low Latency
                          </label>
                          <label className="flex items-center gap-2 text-gray-300">
                            <input
                              type="checkbox"
                              checked={streamForm.auto_record}
                              onChange={(e) => setStreamForm({...streamForm, auto_record: e.target.checked})}
                              className="rounded border-rooam-gray-light"
                            />
                            Auto Record
                          </label>
                        </div>

                        <div className="flex justify-end gap-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => {
                            setShowStreamDialog(false);
                            setEditingStream(null);
                            resetStreamForm();
                          }} className="border-rooam-gray-light text-gray-300">
                            Cancel
                          </Button>
                          <Button type="submit" className="bg-rooam-red hover:bg-red-600 text-white">
                            {editingStream ? 'Update Stream' : 'Create Stream'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent className="p-2 sm:p-6">
                {streams.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <Radio className="h-10 w-10 sm:h-12 sm:w-12 text-gray-500 mx-auto mb-3 sm:mb-4" />
                    <p className="text-gray-400 mb-2 text-sm sm:text-base">No streams configured yet</p>
                    <p className="text-gray-500 text-xs sm:text-sm">Create a stream to get started with live broadcasting</p>
                  </div>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {streams.map((stream) => (
                      <div key={stream.id} className="bg-rooam-black rounded-lg p-3 sm:p-4 border border-rooam-gray-light">
                        <div className="flex flex-col gap-3 sm:gap-4">
                          {/* Header row - always visible */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {getPlatformIcon(stream.platform)}
                              <h3 className="font-semibold text-white text-sm sm:text-base truncate">{stream.stream_name}</h3>
                            </div>
                            <div className="flex-shrink-0">
                              {getStatusBadge(stream.status)}
                            </div>
                          </div>
                          
                          {/* Description */}
                          <p className="text-xs sm:text-sm text-gray-400 line-clamp-2">{stream.description || 'No description'}</p>
                          
                          {/* Credentials - stack on mobile */}
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 text-sm">
                            <div className="bg-rooam-gray-medium rounded p-2">
                              <span className="text-gray-500 block text-xs mb-1">RTMP URL</span>
                              <div className="flex items-center gap-1 sm:gap-2">
                                <code className="text-rooam-blue text-xs truncate flex-1">{stream.rtmp_url}</code>
                                <Button size="icon" variant="ghost" className="h-6 w-6 flex-shrink-0" onClick={() => copyToClipboard(stream.rtmp_url, 'RTMP URL')}>
                                  <Copy className="h-3 w-3 text-gray-400" />
                                </Button>
                              </div>
                            </div>
                            <div className="bg-rooam-gray-medium rounded p-2">
                              <span className="text-gray-500 block text-xs mb-1">Stream Key</span>
                              <div className="flex items-center gap-1">
                                <code className="text-green-400 text-xs flex-1 truncate">
                                  {showStreamKey[stream.id] ? stream.stream_key : (stream.stream_key_preview || '••••••••')}
                                </code>
                                <Button size="icon" variant="ghost" className="h-6 w-6 flex-shrink-0" onClick={() => setShowStreamKey({...showStreamKey, [stream.id]: !showStreamKey[stream.id]})}>
                                  {showStreamKey[stream.id] ? <EyeOff className="h-3 w-3 text-gray-400" /> : <Eye className="h-3 w-3 text-gray-400" />}
                                </Button>
                                <Button size="icon" variant="ghost" className="h-6 w-6 flex-shrink-0" onClick={() => copyToClipboard(stream.stream_key || stream.stream_key_preview, 'Stream Key')}>
                                  <Copy className="h-3 w-3 text-gray-400" />
                                </Button>
                              </div>
                            </div>
                          </div>

                          {stream.playback_url && (
                            <div className="bg-rooam-gray-medium rounded p-2">
                              <span className="text-gray-500 block text-xs mb-1">Playback URL</span>
                              <div className="flex items-center gap-1 sm:gap-2">
                                <code className="text-purple-400 text-xs truncate flex-1">{stream.playback_url}</code>
                                <Button size="icon" variant="ghost" className="h-6 w-6 flex-shrink-0" onClick={() => copyToClipboard(stream.playback_url, 'Playback URL')}>
                                  <Copy className="h-3 w-3 text-gray-400" />
                                </Button>
                              </div>
                            </div>
                          )}

                          {stream.tournament_info && (
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <LinkIcon className="h-3 w-3" />
                              Linked to Tournament: <span className="text-rooam-blue truncate">{stream.tournament_info.name}</span>
                            </div>
                          )}

                          {stream.game_id && (
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <Video className="h-3 w-3" />
                              Linked to Game: <Link to={`/operator/game/${stream.game_id}`} className="text-emerald-400 hover:text-emerald-300 truncate underline">
                                {engineGames.find(g => g.game_id === stream.game_id)?.home_team_name || 'Unknown'} vs {engineGames.find(g => g.game_id === stream.game_id)?.away_team_name || 'Unknown'}
                              </Link>
                              <span className="text-amber-400">(syncs to Operator Console)</span>
                            </div>
                          )}
                          
                          {/* Action buttons - horizontal scroll on mobile */}
                          <div className="flex flex-wrap gap-2 pt-2 border-t border-rooam-gray-light">
                            <Button 
                              size="sm" 
                              className="bg-rooam-blue hover:bg-rooam-blue-light text-black h-8 text-xs"
                              asChild
                            >
                              <Link to={`/watch/${stream.id}`}>
                                <Play className="h-3 w-3 mr-1" />
                                Watch
                              </Link>
                            </Button>
                            <Select value={stream.status} onValueChange={(v) => handleUpdateStreamStatus(stream.id, v)}>
                              <SelectTrigger className="w-20 sm:w-24 bg-rooam-gray-medium border-rooam-gray-light text-white text-xs h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-rooam-gray-medium border-rooam-gray-light">
                                <SelectItem value="idle" className="text-white text-xs">Idle</SelectItem>
                                <SelectItem value="live" className="text-white text-xs">Live</SelectItem>
                                <SelectItem value="ended" className="text-white text-xs">Ended</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="outline" className="border-rooam-gray-light text-gray-300 h-8 text-xs" onClick={() => openEditStream(stream)}>
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10 h-8 text-xs" onClick={() => handleDeleteStream(stream.id)}>
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
              <Card className="bg-rooam-gray-medium border-rooam-gray-light">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Tv className="h-5 w-5 text-rooam-blue" />
                      Events
                    </CardTitle>
                    <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-rooam-blue hover:bg-rooam-blue-light text-black" data-testid="add-event-btn">
                          <Plus className="h-4 w-4 mr-1" />
                          Add Event
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="text-white">Create New Event</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div>
                            <Label className="text-zinc-300">Event Name *</Label>
                            <Input
                              value={eventForm.name}
                              onChange={(e) => setEventForm({...eventForm, name: e.target.value})}
                              placeholder="e.g., Summer Basketball Classic"
                              className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-zinc-300">Sport *</Label>
                              <Select value={eventForm.sport} onValueChange={(v) => setEventForm({...eventForm, sport: v})}>
                                <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                                  <SelectItem value="basketball">Basketball</SelectItem>
                                  <SelectItem value="soccer">Soccer</SelectItem>
                                  <SelectItem value="volleyball">Volleyball</SelectItem>
                                  <SelectItem value="football">Football</SelectItem>
                                  <SelectItem value="multi_sport">Multi-Sport</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-zinc-300">Location *</Label>
                              <Input
                                value={eventForm.location}
                                onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                                placeholder="e.g., Sports Arena"
                                className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-zinc-300">Start Date *</Label>
                              <Input
                                type="date"
                                value={eventForm.date}
                                onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                                className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-300">End Date</Label>
                              <Input
                                type="date"
                                value={eventForm.end_date}
                                onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                                className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-zinc-300">Description</Label>
                            <Textarea
                              value={eventForm.description}
                              onChange={(e) => setEventForm({...eventForm, description: e.target.value})}
                              placeholder="Event description..."
                              className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                              rows={3}
                            />
                          </div>
                          
                          {/* Featured Event Toggle */}
                          <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                            <div>
                              <Label className="text-zinc-200 font-medium">Featured Event</Label>
                              <p className="text-xs text-zinc-500">Show prominently on homepage and events page</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setEventForm({...eventForm, is_featured: !eventForm.is_featured})}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${eventForm.is_featured ? 'bg-cyan-500' : 'bg-zinc-600'}`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${eventForm.is_featured ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                          </div>
                          
                          <Button
                            onClick={async () => {
                              if (!eventForm.name || !eventForm.location || !eventForm.date) {
                                toast.error('Please fill in required fields');
                                return;
                              }
                              try {
                                const res = await fetch(`${API_URL}/api/events`, {
                                  method: 'POST',
                                  headers: { 
                                    'Content-Type': 'application/json',
                                    ...(token && { 'Authorization': `Bearer ${token}` })
                                  },
                                  body: JSON.stringify(eventForm)
                                });
                                if (res.ok) {
                                  toast.success('Event created successfully!');
                                  setShowEventDialog(false);
                                  setEventForm({ name: '', sport: 'basketball', location: '', date: '', end_date: '', description: '', status: 'upcoming', is_featured: false });
                                  fetchData();
                                } else {
                                  const err = await res.json();
                                  toast.error(err.detail || 'Failed to create event');
                                }
                              } catch (err) {
                                toast.error('Failed to create event');
                              }
                            }}
                            className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black"
                          >
                            Create Event
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {allEvents.length === 0 ? (
                    <p className="text-gray-400 text-center py-4">No events found</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {allEvents.map(event => (
                        <div
                          key={event.id}
                          data-testid={`event-card-${event.id}`}
                          className={`p-3 rounded-lg bg-rooam-black hover:bg-rooam-gray-light transition-all ${event.hidden ? 'opacity-50 border border-yellow-500/30' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium text-sm">{event.name}</h4>
                            {event.hidden && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-bold uppercase">Hidden</span>}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                            <MapPin className="h-3 w-3 shrink-0" />
                            <span className="truncate">{event.location || 'TBD'}</span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge className="bg-cyan-500/20 text-cyan-400 text-xs capitalize">
                              {event.sport || 'Sports'}
                            </Badge>
                            {event.courts?.length > 0 && (
                              <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                                {event.courts.length} Courts
                              </Badge>
                            )}
                            {event.date && (
                              <span className="text-[10px] text-gray-500">{safeDateStr(event.date)}</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            <Link to={`/game-day/${event.id}`}>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-amber-400 hover:bg-amber-500/10"
                                data-testid={`game-day-event-${event.id}`}
                              >
                                <Radio className="h-3 w-3 mr-1" /> Game Day
                              </Button>
                            </Link>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-blue-400 hover:bg-blue-500/10"
                              onClick={() => openEditEvent(event)}
                              data-testid={`edit-event-${event.id}`}
                            >
                              <Edit className="h-3 w-3 mr-1" /> Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-7 px-2 ${event.hidden ? 'text-green-400 hover:bg-green-500/10' : 'text-yellow-400 hover:bg-yellow-500/10'}`}
                              onClick={async () => {
                                try {
                                  const authToken = token || localStorage.getItem('token');
                                  const res = await fetch(`${API_URL}/api/events/${event.id}/visibility`, {
                                    method: 'PUT',
                                    headers: { 'Authorization': `Bearer ${authToken}` }
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    toast.success(data.message);
                                    setAllEvents(allEvents.map(e => e.id === event.id ? { ...e, hidden: data.hidden } : e));
                                  } else {
                                    toast.error('Failed to toggle visibility');
                                  }
                                } catch (err) {
                                  toast.error('Failed to toggle visibility');
                                }
                              }}
                              data-testid={`toggle-visibility-event-${event.id}`}
                            >
                              {event.hidden ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
                              {event.hidden ? 'Show' : 'Hide'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-7 px-2 ${event.free_with_account ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-zinc-400 hover:bg-zinc-500/10'}`}
                              onClick={async () => {
                                try {
                                  const authToken = token || localStorage.getItem('token');
                                  const res = await fetch(`${API_URL}/api/events/${event.id}/free-with-account`, {
                                    method: 'PATCH',
                                    headers: { 'Authorization': `Bearer ${authToken}` }
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    toast.success(data.free_with_account ? 'Now FREE with account' : 'Free access disabled');
                                    setAllEvents(allEvents.map(e => e.id === event.id ? { ...e, free_with_account: data.free_with_account } : e));
                                  } else {
                                    toast.error('Failed to toggle free access');
                                  }
                                } catch (err) {
                                  toast.error('Failed to toggle free access');
                                }
                              }}
                              data-testid={`toggle-free-with-account-${event.id}`}
                              title="Toggle: any logged-in user can watch for free"
                            >
                              {event.free_with_account ? <Check className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
                              {event.free_with_account ? 'Free' : 'Paid'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-cyan-400 hover:bg-cyan-500/10"
                              onClick={async () => {
                                const toastId = toast.loading('Pushing divisions to SportsVisio...');
                                try {
                                  const authToken = token || localStorage.getItem('token');
                                  const res = await fetch(`${API_URL}/api/sportsvisio/events/${event.id}/push-divisions`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' },
                                  });
                                  if (res.ok) {
                                    const data = await res.json();
                                    toast.success(
                                      `Created ${data.created}, matched ${data.skipped} existing`,
                                      { id: toastId, description: data.errors > 0 ? `${data.errors} error(s) - check logs` : undefined }
                                    );
                                  } else {
                                    const err = await res.json().catch(() => ({}));
                                    toast.error(err.detail || 'Push failed', { id: toastId });
                                  }
                                } catch {
                                  toast.error('Push failed', { id: toastId });
                                }
                              }}
                              data-testid={`push-divisions-${event.id}`}
                              title="Push Rooam divisions to SportsVisio (requires SV link)"
                            >
                              <ArrowUpCircle className="h-3 w-3 mr-1" /> Push Divisions
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-red-400 hover:bg-red-500/10"
                              onClick={() => handleDeleteEvent(event.id, event.name)}
                              data-testid={`delete-event-${event.id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" /> Delete
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-purple-400 hover:bg-purple-500/10"
                              onClick={() => openPassDialog(event)}
                              data-testid={`passes-event-${event.id}`}
                            >
                              <Ticket className="h-3 w-3 mr-1" /> Passes
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-emerald-400 hover:bg-emerald-500/10"
                              onClick={() => { setManageEvent(event); setShowManageDialog(true); }}
                              data-testid={`manage-event-${event.id}`}
                            >
                              <Layers className="h-3 w-3 mr-1" /> Divisions
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-pink-400 hover:bg-pink-500/10"
                              onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'file';
                                input.accept = 'image/*';
                                input.onchange = async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  try {
                                    const res = await fetch(`${API_URL}/api/uploads/event-logo/${event.id}`, {
                                      method: 'POST',
                                      headers: { 'Authorization': `Bearer ${token}` },
                                      body: formData,
                                    });
                                    if (res.ok) {
                                      toast.success('Event logo uploaded');
                                      fetchData();
                                    } else {
                                      toast.error('Failed to upload logo');
                                    }
                                  } catch { toast.error('Upload error'); }
                                };
                                input.click();
                              }}
                              data-testid={`logo-event-${event.id}`}
                            >
                              <Upload className="h-3 w-3 mr-1" /> Logo
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Event Pass Management Dialog */}
              <Dialog open={showPassDialog} onOpenChange={setShowPassDialog}>
                <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                      <Ticket className="h-5 w-5 text-purple-400" />
                      Event Passes — {passEventName}
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                      Create pass tiers for customers to purchase access to this event without a membership.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    {/* Existing Tiers */}
                    {passTiers.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Current Tiers</p>
                        {passTiers.map(tier => (
                          <div key={tier.id} className="flex items-center justify-between p-3 bg-rooam-black rounded-lg" data-testid={`admin-pass-${tier.id}`}>
                            <div>
                              <p className="text-white font-medium text-sm">{tier.name}</p>
                              {tier.description && <p className="text-xs text-gray-500 mt-0.5">{tier.description}</p>}
                              {tier.valid_days?.length > 0 && <p className="text-[10px] text-zinc-600 mt-0.5">Days: {tier.valid_days.join(', ')}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-[#FF453A]">${tier.price}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeletePass(tier.id)}
                                data-testid={`delete-pass-${tier.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Create New Tier Form */}
                    <div className="border-t border-white/10 pt-4 space-y-3">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">Add New Pass Tier</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-gray-300 text-xs">Name *</Label>
                          <Input
                            value={passForm.name}
                            onChange={e => setPassForm({...passForm, name: e.target.value})}
                            placeholder="e.g., Full Weekend Pass"
                            className="bg-rooam-black border-rooam-gray-light text-white text-sm mt-1"
                            data-testid="pass-name-input"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300 text-xs">Price ($) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={passForm.price}
                            onChange={e => setPassForm({...passForm, price: e.target.value})}
                            placeholder="30.00"
                            className="bg-rooam-black border-rooam-gray-light text-white text-sm mt-1"
                            data-testid="pass-price-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-300 text-xs">Description</Label>
                        <Input
                          value={passForm.description}
                          onChange={e => setPassForm({...passForm, description: e.target.value})}
                          placeholder="e.g., Access to all Saturday & Sunday games"
                          className="bg-rooam-black border-rooam-gray-light text-white text-sm mt-1"
                          data-testid="pass-description-input"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300 text-xs">Valid Days (optional, comma-separated dates)</Label>
                        <Input
                          value={passForm.valid_days}
                          onChange={e => setPassForm({...passForm, valid_days: e.target.value})}
                          placeholder="e.g., 2026-04-12, 2026-04-13"
                          className="bg-rooam-black border-rooam-gray-light text-white text-sm mt-1"
                          data-testid="pass-days-input"
                        />
                      </div>
                      <Button
                        onClick={handleCreatePass}
                        disabled={savingPass}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium"
                        data-testid="create-pass-btn"
                      >
                        {savingPass ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                        {savingPass ? 'Creating...' : 'Create Pass Tier'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Division & Team Management Dialog */}
              <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
                <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-2xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                      <Layers className="h-5 w-5 text-emerald-400" />
                      {manageEvent?.name} — Divisions & Teams
                    </DialogTitle>
                    <DialogDescription className="text-gray-400">
                      Organize your event into divisions and add teams to each division. This structure syncs with SportsVisio.
                    </DialogDescription>
                  </DialogHeader>
                  {manageEvent && (
                    <EventDivisionsManager eventId={manageEvent.id} token={token} onUpdate={fetchData} />
                  )}
                </DialogContent>
              </Dialog>

          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-white" data-testid="user-management-title">User Management</CardTitle>
                  <div className="flex items-center gap-2">
                    {isSuperAdmin() && (
                      <Button
                        className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold h-9 px-3 text-sm"
                        onClick={() => setShowAddMemberDialog(true)}
                        data-testid="add-member-btn"
                      >
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add Member
                      </Button>
                    )}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                      <Input
                        placeholder="Search users..."
                        className="pl-10 bg-rooam-black border-rooam-gray-light text-white w-full sm:w-64"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="user-search-input"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="users-table">
                    <thead>
                      <tr className="border-b border-rooam-gray-light">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Name</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Role</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((u) => (
                        <tr key={u.id} className={`border-b border-rooam-gray-light hover:bg-rooam-black/30 ${u.suspended ? 'opacity-60' : ''}`} data-testid={`user-row-${u.id}`}>
                          <td className="py-3 px-4 text-white">
                            <div className="flex items-center gap-2">
                              {u.name}
                              {u.role === 'super_admin' && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-bold uppercase tracking-wider">Super</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-300">{u.email}</td>
                          <td className="py-3 px-4">
                            {isSuperAdmin() ? (
                              <select
                                value={u.role}
                                onChange={(e) => handleUpdateRole(u.id, e.target.value)}
                                className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white text-sm appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
                                style={{ colorScheme: 'dark' }}
                                disabled={u.id === user?.id}
                                data-testid={`role-select-${u.id}`}
                              >
                                {['super_admin', 'admin', 'operator', 'team_admin', 'college_coach', 'athlete', 'coach', 'parent', 'scout', 'creator'].map(role => (
                                  <option key={role} value={role} className="bg-gray-900 text-white">
                                    {role === 'super_admin' ? 'Super Admin' : role === 'team_admin' ? 'Team Admin' : role === 'college_coach' ? 'College Coach' : role.charAt(0).toUpperCase() + role.slice(1)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className={`text-sm px-2 py-1 rounded ${
                                u.role === 'super_admin' ? 'bg-amber-500/20 text-amber-400' :
                                u.role === 'admin' ? 'bg-red-500/20 text-red-400' :
                                u.role === 'operator' ? 'bg-blue-500/20 text-blue-400' :
                                'bg-gray-700 text-gray-300'
                              }`}>
                                {u.role === 'super_admin' ? 'Super Admin' : u.role === 'team_admin' ? 'Team Admin' : u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-1 rounded font-medium ${
                              u.suspended ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                            }`} data-testid={`user-status-${u.id}`}>
                              {u.suspended ? 'Suspended' : 'Active'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                                onClick={() => openEditUser(u)}
                                data-testid={`edit-user-${u.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                                onClick={() => openResetPassword(u)}
                                data-testid={`reset-password-${u.id}`}
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              {isSuperAdmin() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                                  onClick={() => { setUpgradeUser(u); setUpgradePlan('plus_quarterly'); }}
                                  title="Upgrade membership (free)"
                                  data-testid={`upgrade-user-${u.id}`}
                                >
                                  <ArrowUpCircle className="h-4 w-4" />
                                </Button>
                              )}
                              {isSuperAdmin() && u.id !== user?.id && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={u.suspended ? "text-green-400 hover:text-green-300 hover:bg-green-500/10" : "text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"}
                                    onClick={() => handleSuspendUser(u.id)}
                                    title={u.suspended ? 'Unsuspend user' : 'Suspend user'}
                                    data-testid={`suspend-user-${u.id}`}
                                  >
                                    {u.suspended ? <ShieldCheck className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                                  </Button>
                                  {u.role !== 'super_admin' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                      onClick={() => handleDeleteUser(u.id)}
                                      data-testid={`delete-user-${u.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Rosters Tab */}
          <TabsContent value="rosters">
            <RostersManager token={token} />
          </TabsContent>
          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <CardTitle className="text-white">Payment Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-rooam-gray-light">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Session ID</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Package</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Amount</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((t) => (
                          <tr key={t.id} className="border-b border-rooam-gray-light hover:bg-rooam-black/30">
                            <td className="py-3 px-4 text-gray-300 font-mono text-xs">
                              {t.session_id?.slice(0, 20)}...
                            </td>
                            <td className="py-3 px-4 text-white">{t.package_id}</td>
                            <td className="py-3 px-4 text-rooam-blue font-semibold">${t.amount}</td>
                            <td className="py-3 px-4">
                              <Badge className={
                                t.payment_status === 'paid' 
                                  ? 'bg-green-500/20 text-green-400 border-green-500'
                                  : t.payment_status === 'initiated'
                                  ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500'
                                  : 'bg-red-500/20 text-red-400 border-red-500'
                              }>
                                {t.payment_status}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-gray-300">
                              {new Date(t.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* VODs Tab */}
          <TabsContent value="vods">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Video className="h-5 w-5 text-rooam-blue" />
                    VOD Recordings
                  </CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                      data-testid="sync-recent-vods-btn"
                      onClick={async () => {
                        const toastId = toast.loading('Scanning recent games for replays...');
                        try {
                          const res = await fetch(`${API_URL}/api/court-streams/sync-recent-vods`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (res.ok) {
                            const data = await res.json();
                            toast.success(
                              `Scanned ${data.scanned} — Attached ${data.fixed} replay(s)`,
                              { id: toastId, description: data.fixed > 0 ? 'Replays are now available on those games.' : 'All recordings were already attached.' }
                            );
                            fetchVODs();
                          } else {
                            toast.error('Sync failed', { id: toastId });
                          }
                        } catch {
                          toast.error('Sync failed', { id: toastId });
                        }
                      }}
                    >
                      <Zap className="h-4 w-4 mr-1" />
                      Sync Recent Replays
                    </Button>
                    <Button size="sm" variant="outline" className="border-rooam-gray-light text-gray-300" onClick={fetchVODs}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingStreams ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-rooam-blue" />
                  </div>
                ) : vodList.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <Video className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No VOD recordings found</p>
                    <p className="text-xs mt-1">Recordings will appear here after games are streamed via Cloudflare</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {vodList.map(vod => (
                      <div key={vod.uid} className="bg-rooam-black rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                        {vod.thumbnail && (
                          <img src={vod.thumbnail} alt="" className="w-32 h-20 rounded object-cover bg-zinc-800 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">{vod.meta?.name || vod.uid?.slice(0,16)}</div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                            {vod.duration && <span>{Math.round(vod.duration / 60)}m</span>}
                            <span>{vod.status}</span>
                            {vod.created && <span>{new Date(vod.created).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {vod.playback_url && (
                            <Button size="sm" variant="outline" className="border-rooam-gray-light text-gray-300 text-xs" asChild>
                              <a href={vod.playback_url} target="_blank" rel="noopener noreferrer">
                                <Play className="h-3 w-3 mr-1" /> Play
                              </a>
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="border-zinc-600 text-zinc-400 text-xs" onClick={() => {
                            navigator.clipboard.writeText(vod.playback_url || '');
                            toast.success('URL copied');
                          }}>
                            <Copy className="h-3 w-3 mr-1" /> URL
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-800 text-red-400 hover:bg-red-500/10 text-xs" data-testid={`delete-vod-${vod.uid}`} onClick={async () => {
                            if (!window.confirm(`Delete "${vod.meta?.name || vod.uid?.slice(0,16)}"? This cannot be undone.`)) return;
                            try {
                              const authToken = token || sessionStorage.getItem('rooam_token');
                              const res = await fetch(`${API_URL}/api/cloudflare/videos/${vod.uid}`, {
                                method: 'DELETE',
                                headers: { Authorization: `Bearer ${authToken}` },
                              });
                              if (res.ok) {
                                toast.success('VOD deleted');
                                setVodList(prev => prev.filter(v => v.uid !== vod.uid));
                              } else {
                                toast.error('Failed to delete VOD');
                              }
                            } catch {
                              toast.error('Failed to delete VOD');
                            }
                          }}>
                            <Trash2 className="h-3 w-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="text-xs text-zinc-600 pt-1">{vodList.length} recordings</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Clips Management */}
            <Card className="bg-rooam-gray-medium border-rooam-gray-light mt-4">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Scissors className="h-5 w-5 text-red-500" />
                    Highlight Clips ({clipsList.length})
                  </CardTitle>
                  <Button size="sm" variant="outline" className="border-rooam-gray-light text-gray-300" onClick={fetchClips}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {clipsList.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <Scissors className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p>No clips created yet</p>
                    <p className="text-xs mt-1">Viewers can create clips from the game watch page</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {clipsList.map(clip => (
                      <div key={clip.clip_id} className="bg-rooam-black rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">{clip.title}</div>
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-zinc-500">
                            <span>{clip.home_team} vs {clip.away_team}</span>
                            <span className="text-zinc-700">|</span>
                            <span>{clip.duration}s</span>
                            <span className="text-zinc-700">|</span>
                            <span>{clip.views || 0} views</span>
                            <span className="text-zinc-700">|</span>
                            <span>{clip.likes || 0} likes</span>
                            <span className="text-zinc-700">|</span>
                            <span>{clip.shared_count || 0} shares</span>
                          </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" variant="outline" className="border-zinc-600 text-zinc-400 text-xs" onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/clip/${clip.clip_id}`);
                            toast.success('Clip link copied');
                          }}>
                            <Copy className="h-3 w-3 mr-1" /> Link
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-800 text-red-400 text-xs hover:bg-red-900/30" onClick={() => deleteClip(clip.clip_id)}>
                            <Trash2 className="h-3 w-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SportsVisio AI Stats Tab */}
          <TabsContent value="sportsvisio">
            <SportsVisioManager />
          </TabsContent>

          {/* Discounts Tab */}
          <TabsContent value="discounts">
            <Card className="bg-rooam-gray-medium border-rooam-gray-light">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white" data-testid="discounts-title">Discount Codes</CardTitle>
                  <Button className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold h-9 px-3 text-sm" onClick={() => setShowDiscountDialog(true)} data-testid="create-discount-btn">
                    <Plus className="h-4 w-4 mr-1.5" />
                    Create Code
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {discountCodes.length === 0 ? (
                  <div className="text-center py-12">
                    <Tag className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400">No discount codes yet</p>
                    <p className="text-zinc-600 text-sm mt-1">Create a code to offer discounts on memberships or event passes.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {discountCodes.map(dc => (
                      <div key={dc.id} className={`flex items-center justify-between p-3 rounded-lg border ${dc.active ? 'bg-zinc-900/50 border-zinc-700' : 'bg-zinc-900/20 border-zinc-800 opacity-60'}`} data-testid={`discount-row-${dc.id}`}>
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div>
                            <p className="text-white font-mono font-bold text-sm tracking-wider">{dc.code}</p>
                            {dc.description && <p className="text-zinc-500 text-xs mt-0.5">{dc.description}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <span className="text-emerald-400 font-bold text-sm">{dc.discount_percent}% OFF</span>
                          <span className="text-zinc-500 text-xs">{dc.times_used || 0}{dc.max_uses ? `/${dc.max_uses}` : ''} used</span>
                          <Button variant="ghost" size="sm" className={`h-7 px-2 text-xs ${dc.active ? 'text-emerald-400 hover:text-red-400' : 'text-zinc-500 hover:text-emerald-400'}`} onClick={() => toggleDiscount(dc.id)} data-testid={`toggle-discount-${dc.id}`}>
                            {dc.active ? 'Active' : 'Disabled'}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-500 hover:text-red-400" onClick={() => deleteDiscount(dc.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Programs Tab - Super Admin Program Management */}
          <TabsContent value="programs">
            <ProgramsManager token={token} />
          </TabsContent>

          {/* Leads Tab - Email Signups & Source Attribution */}
          <TabsContent value="leads">
            {isSuperAdmin && <SeedArticlesButton token={token} />}
            <LeadsManager token={token} />
          </TabsContent>

          {/* Analytics Tab - Cloudflare library + DB engagement + audience */}
          <TabsContent value="analytics">
            <AnalyticsManager token={token} />
          </TabsContent>
        </Tabs>

        {/* Edit Game Dialog */}
        <Dialog open={showEditGameDialog} onOpenChange={(open) => { setShowEditGameDialog(open); if (!open) setEditingGame(null); }}>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-400" />
                Edit Game
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-300">Event (optional)</Label>
                <Select value={gameForm.event_id || "none"} onValueChange={(v) => setGameForm({...gameForm, event_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                    <SelectValue placeholder="Select an event..." />
                  </SelectTrigger>
                  <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                    <SelectItem value="none">No Event (Standalone Game)</SelectItem>
                    {allEvents.map(evt => (
                      <SelectItem key={evt.id} value={evt.id}>{evt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Game Title</Label>
                <Input
                  value={gameForm.title}
                  onChange={(e) => setGameForm({...gameForm, title: e.target.value})}
                  placeholder="e.g., Championship Finals"
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Home Team *</Label>
                  <Input
                    value={gameForm.home_team_name}
                    onChange={(e) => setGameForm({...gameForm, home_team_name: e.target.value})}
                    placeholder="Home Team"
                    className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Away Team *</Label>
                  <Input
                    value={gameForm.away_team_name}
                    onChange={(e) => setGameForm({...gameForm, away_team_name: e.target.value})}
                    placeholder="Away Team"
                    className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Sport</Label>
                  <Select value={gameForm.sport} onValueChange={(v) => setGameForm({...gameForm, sport: v})}>
                    <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                      <SelectItem value="basketball">Basketball</SelectItem>
                      <SelectItem value="soccer">Soccer</SelectItem>
                      <SelectItem value="volleyball">Volleyball</SelectItem>
                      <SelectItem value="football">Football</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Location</Label>
                  <Input
                    value={gameForm.location}
                    onChange={(e) => setGameForm({...gameForm, location: e.target.value})}
                    placeholder="Venue/Court"
                    className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                  />
                </div>
              </div>
              <Button onClick={handleUpdateGame} className="w-full bg-blue-600 hover:bg-blue-500 text-white">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Event Dialog */}
        <Dialog open={showEditEventDialog} onOpenChange={(open) => { setShowEditEventDialog(open); if (!open) setEditingEvent(null); }}>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Edit className="h-5 w-5 text-purple-400" />
                Edit Event
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-300">Event Name *</Label>
                <Input
                  value={eventForm.name}
                  onChange={(e) => setEventForm({...eventForm, name: e.target.value})}
                  placeholder="e.g., Summer Basketball Classic"
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Sport *</Label>
                  <Select value={eventForm.sport} onValueChange={(v) => setEventForm({...eventForm, sport: v})}>
                    <SelectTrigger className="bg-rooam-gray-dark border-rooam-gray-light text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-rooam-gray-dark border-rooam-gray-light">
                      <SelectItem value="basketball">Basketball</SelectItem>
                      <SelectItem value="soccer">Soccer</SelectItem>
                      <SelectItem value="volleyball">Volleyball</SelectItem>
                      <SelectItem value="football">Football</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Location *</Label>
                  <Input
                    value={eventForm.location}
                    onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                    placeholder="Sports Arena"
                    className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Start Date *</Label>
                  <Input
                    type="date"
                    value={eventForm.date}
                    onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                    className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">End Date</Label>
                  <Input
                    type="date"
                    value={eventForm.end_date}
                    onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                    className="bg-rooam-gray-dark border-rooam-gray-light text-white"
                  />
                </div>
              </div>
              <Button onClick={handleUpdateEvent} className="w-full bg-purple-600 hover:bg-purple-500 text-white">
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={showEditUserDialog} onOpenChange={(open) => { setShowEditUserDialog(open); if (!open) setEditingUser(null); }}>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-400" />
                Edit User
              </DialogTitle>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300">Name</Label>
                  <Input
                    value={editingUser.name || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Email</Label>
                  <Input
                    type="email"
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Role</Label>
                  <select
                    value={editingUser.role || 'athlete'}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    className="w-full bg-rooam-black border border-rooam-gray-light rounded-md px-3 py-2 text-white mt-1"
                  >
                    {['athlete', 'coach', 'parent', 'scout', 'creator', 'admin'].map(role => (
                      <option key={role} value={role} className="bg-rooam-black text-white">{role}</option>
                    ))}
                  </select>
                </div>
                <Button onClick={handleUpdateUser} className="w-full bg-blue-600 hover:bg-blue-500 text-white">
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={showResetPasswordDialog} onOpenChange={(open) => { setShowResetPasswordDialog(open); if (!open) { setResetPasswordUser(null); setNewPassword(''); } }}>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Key className="h-5 w-5 text-yellow-400" />
                Reset Password
              </DialogTitle>
            </DialogHeader>
            {resetPasswordUser && (
              <div className="space-y-4">
                <div className="bg-rooam-black rounded-lg p-3">
                  <p className="text-sm text-gray-400">Resetting password for:</p>
                  <p className="text-white font-medium">{resetPasswordUser.name}</p>
                  <p className="text-gray-500 text-sm">{resetPasswordUser.email}</p>
                </div>
                <div>
                  <Label className="text-gray-300">New Password</Label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 chars)"
                    className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  />
                </div>
                <Button onClick={handleResetPassword} className="w-full bg-yellow-600 hover:bg-yellow-500 text-black font-semibold">
                  Reset Password
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Discount Dialog */}
        {/* Upgrade User Dialog */}
        <Dialog open={!!upgradeUser} onOpenChange={(open) => { if (!open) setUpgradeUser(null); }}>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <ArrowUpCircle className="h-5 w-5 text-cyan-400" />
                Upgrade Membership (Free)
              </DialogTitle>
            </DialogHeader>
            {upgradeUser && (
              <div className="space-y-4">
                <div className="bg-rooam-black rounded-lg p-3">
                  <p className="text-sm text-gray-400">Upgrading:</p>
                  <p className="text-white font-medium">{upgradeUser.name}</p>
                  <p className="text-gray-500 text-sm">{upgradeUser.email}</p>
                </div>
                <div>
                  <Label className="text-gray-300">Select Plan</Label>
                  <select
                    value={upgradePlan}
                    onChange={(e) => setUpgradePlan(e.target.value)}
                    className="w-full mt-1 bg-rooam-black border border-rooam-gray-light text-white rounded-md px-3 py-2 text-sm"
                    data-testid="upgrade-plan-select"
                  >
                    <option value="basic_quarterly">Basic — $8.99/quarter</option>
                    <option value="basic_yearly">Basic — $35.96/year</option>
                    <option value="plus_quarterly">Plus — $29.99/quarter</option>
                    <option value="plus_yearly">Plus — $119.96/year</option>
                    <option value="elite_quarterly">Elite — $74.99/quarter</option>
                    <option value="elite_yearly">Elite — $299.96/year</option>
                  </select>
                </div>
                <p className="text-xs text-zinc-500">This will activate the membership immediately with no charge.</p>
                <Button onClick={handleUpgradeUser} className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold" data-testid="confirm-upgrade-btn">
                  Upgrade Now (No Charge)
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Discount Dialog */}
        <Dialog open={showDiscountDialog} onOpenChange={setShowDiscountDialog}>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Tag className="h-5 w-5 text-emerald-400" />
                Create Discount Code
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Create a promo code for memberships or event passes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-gray-300">Code *</Label>
                <Input
                  value={discountForm.code}
                  onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                  placeholder="e.g. SPRING2026"
                  className="bg-rooam-black border-rooam-gray-light text-white mt-1 font-mono tracking-wider"
                  data-testid="discount-code-input"
                />
              </div>
              <div>
                <Label className="text-gray-300">Discount Percentage</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={discountForm.discount_percent}
                  onChange={(e) => setDiscountForm({ ...discountForm, discount_percent: parseInt(e.target.value) || 0 })}
                  className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  data-testid="discount-percent-input"
                />
                <p className="text-zinc-500 text-xs mt-1">100% = free, 50% = half off</p>
              </div>
              <div>
                <Label className="text-gray-300">Max Uses (0 = unlimited)</Label>
                <Input
                  type="number"
                  min="0"
                  value={discountForm.max_uses}
                  onChange={(e) => setDiscountForm({ ...discountForm, max_uses: parseInt(e.target.value) || 0 })}
                  className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  data-testid="discount-max-uses-input"
                />
              </div>
              <div>
                <Label className="text-gray-300">Description (optional)</Label>
                <Input
                  value={discountForm.description}
                  onChange={(e) => setDiscountForm({ ...discountForm, description: e.target.value })}
                  placeholder="e.g. Early bird spring special"
                  className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  data-testid="discount-desc-input"
                />
              </div>
              <Button
                onClick={handleCreateDiscount}
                disabled={savingDiscount || !discountForm.code}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                data-testid="discount-submit-btn"
              >
                {savingDiscount ? 'Creating...' : 'Create Discount Code'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Member Dialog */}
        <Dialog open={showAddMemberDialog} onOpenChange={(open) => { setShowAddMemberDialog(open); if (!open) setAddMemberForm({ name: '', email: '', role: 'athlete', phone: '', program: '', bypass_payment: false }); }}>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light max-w-md max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Plus className="h-5 w-5 text-rooam-blue" />
                Add New Member
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Create a new account and send login credentials via email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-gray-300">Full Name *</Label>
                <Input
                  value={addMemberForm.name}
                  onChange={(e) => setAddMemberForm({ ...addMemberForm, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  data-testid="add-member-name"
                />
              </div>
              <div>
                <Label className="text-gray-300">Email *</Label>
                <Input
                  type="email"
                  value={addMemberForm.email}
                  onChange={(e) => setAddMemberForm({ ...addMemberForm, email: e.target.value })}
                  placeholder="john@example.com"
                  className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  data-testid="add-member-email"
                />
              </div>
              <div>
                <Label className="text-gray-300">Role *</Label>
                <select
                  value={addMemberForm.role}
                  onChange={(e) => {
                    const role = e.target.value;
                    const nonCustomer = ['admin', 'operator', 'team_admin', 'super_admin', 'creator'];
                    setAddMemberForm({ ...addMemberForm, role, bypass_payment: nonCustomer.includes(role) ? false : addMemberForm.bypass_payment });
                  }}
                  className="w-full mt-1 bg-rooam-black border border-rooam-gray-light rounded-md px-3 py-2 text-white text-sm appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
                  style={{ colorScheme: 'dark' }}
                  data-testid="add-member-role"
                >
                  <optgroup label="Admin Roles (No membership needed)">
                    <option value="super_admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="operator">Operator</option>
                    <option value="team_admin">Team Admin</option>
                  </optgroup>
                  <optgroup label="Member Roles">
                    <option value="athlete">Athlete</option>
                    <option value="coach">Coach</option>
                    <option value="parent">Parent</option>
                    <option value="scout">Scout</option>
                    <option value="college_coach">College Coach</option>
                    <option value="creator">Creator</option>
                  </optgroup>
                </select>
              </div>
              <div>
                <Label className="text-gray-300">Phone</Label>
                <Input
                  type="tel"
                  value={addMemberForm.phone}
                  onChange={(e) => setAddMemberForm({ ...addMemberForm, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  data-testid="add-member-phone"
                />
              </div>
              <div>
                <Label className="text-gray-300">Program / Organization</Label>
                <Input
                  value={addMemberForm.program}
                  onChange={(e) => setAddMemberForm({ ...addMemberForm, program: e.target.value })}
                  placeholder="e.g. AAU Basketball, Local League"
                  className="bg-rooam-black border-rooam-gray-light text-white mt-1"
                  data-testid="add-member-program"
                />
              </div>

              {['athlete', 'coach', 'parent', 'scout', 'college_coach'].includes(addMemberForm.role) && (
                <div className="bg-rooam-black rounded-lg p-3 border border-rooam-gray-light">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="add-member-bypass-payment">
                    <input
                      type="checkbox"
                      checked={addMemberForm.bypass_payment}
                      onChange={(e) => setAddMemberForm({ ...addMemberForm, bypass_payment: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-rooam-blue focus:ring-rooam-blue accent-[#00d4ff]"
                    />
                    <div>
                      <p className="text-white text-sm font-medium">Bypass Payment</p>
                      <p className="text-gray-500 text-xs">Grant full access without requiring membership payment</p>
                    </div>
                  </label>
                </div>
              )}

              <Button
                onClick={handleAddMember}
                disabled={addingMember || !addMemberForm.name || !addMemberForm.email}
                className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold"
                data-testid="add-member-submit"
              >
                {addingMember ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Adding...</> : 'Add Member & Send Invite'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Game Stream Settings Dialog */}
        <Dialog open={showGameStreamDialog} onOpenChange={setShowGameStreamDialog}>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light text-white max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Video className="h-5 w-5 text-cyan-400" />
                Stream Settings
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {gameStreamForm.game_title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Platform Quick Select */}
              <div>
                <Label className="text-zinc-400 text-sm mb-2 block">Streaming Platform</Label>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    type="button"
                    onClick={() => setGameStreamForm({...gameStreamForm, custom_rtmp_url: 'rtmps://live.cloudflare.com:443/live/'})}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${gameStreamForm.custom_rtmp_url?.includes('cloudflare') ? 'bg-orange-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                  >
                    Cloudflare
                  </button>
                  <button
                    type="button"
                    onClick={() => setGameStreamForm({...gameStreamForm, custom_rtmp_url: 'rtmp://'})}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${gameStreamForm.custom_rtmp_url && !gameStreamForm.custom_rtmp_url.includes('cloudflare') && !gameStreamForm.custom_rtmp_url.includes('youtube') && !gameStreamForm.custom_rtmp_url.includes('twitch') ? 'bg-cyan-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                  >
                    Custom
                  </button>
                  <button
                    type="button"
                    onClick={() => setGameStreamForm({...gameStreamForm, custom_rtmp_url: 'rtmp://a.rtmp.youtube.com/live2/'})}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${gameStreamForm.custom_rtmp_url?.includes('youtube') ? 'bg-red-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                  >
                    YouTube
                  </button>
                  <button
                    type="button"
                    onClick={() => setGameStreamForm({...gameStreamForm, custom_rtmp_url: 'rtmp://live.twitch.tv/app/'})}
                    className={`px-2 py-1.5 rounded text-xs font-medium transition-all ${gameStreamForm.custom_rtmp_url?.includes('twitch') ? 'bg-purple-600 text-white' : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'}`}
                  >
                    Twitch
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-zinc-400 text-sm">RTMP Server URL</Label>
                <Input
                  value={gameStreamForm.custom_rtmp_url}
                  onChange={(e) => setGameStreamForm({...gameStreamForm, custom_rtmp_url: e.target.value})}
                  placeholder="rtmps://live.cloudflare.com:443/live/"
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white mt-1"
                />
              </div>

              <div>
                <Label className="text-zinc-400 text-sm">Stream Key</Label>
                <Input
                  value={gameStreamForm.custom_stream_key}
                  onChange={(e) => setGameStreamForm({...gameStreamForm, custom_stream_key: e.target.value})}
                  placeholder="your-stream-key"
                  className="bg-rooam-gray-dark border-rooam-gray-light text-white mt-1"
                />
              </div>

              <div className="bg-cyan-900/20 border border-cyan-700 rounded-lg p-3">
                <Label className="text-cyan-400 text-sm font-medium">📺 Playback URL (What viewers watch)</Label>
                <Input
                  value={gameStreamForm.playback_url}
                  onChange={(e) => setGameStreamForm({...gameStreamForm, playback_url: e.target.value})}
                  placeholder="https://customer-xxx.cloudflarestream.com/.../video.m3u8"
                  className="bg-rooam-gray-dark border-cyan-600 text-white mt-2"
                />
                <p className="text-xs text-cyan-300 mt-2">
                  HLS (.m3u8) or direct video URL that viewers will use to watch the stream
                </p>
              </div>

              <Button
                onClick={async () => {
                  try {
                    const authToken = token || localStorage.getItem('token');
                    
                    // First, try to update existing stream session's playback URL
                    if (gameStreamForm.playback_url) {
                      const playbackRes = await fetch(`${API_URL}/api/engine/stream/sessions/${gameStreamForm.game_id}/playback-url`, {
                        method: 'PATCH',
                        headers: { 
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${authToken}` 
                        },
                        body: JSON.stringify({ playback_url: gameStreamForm.playback_url })
                      });
                      
                      if (playbackRes.ok) {
                        toast.success('Stream settings updated!');
                        setShowGameStreamDialog(false);
                        fetchData();
                        return;
                      }
                    }
                    
                    // If no existing session or playback update failed, create/update full stream session
                    const res = await fetch(`${API_URL}/api/engine/stream/sessions?game_id=${gameStreamForm.game_id}`, {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}` 
                      },
                      body: JSON.stringify({
                        custom_rtmp_url: gameStreamForm.custom_rtmp_url,
                        custom_stream_key: gameStreamForm.custom_stream_key,
                        playback_url: gameStreamForm.playback_url
                      })
                    });
                    
                    if (res.ok) {
                      toast.success('Stream settings saved!');
                      setShowGameStreamDialog(false);
                      fetchData();
                    } else {
                      const err = await res.json();
                      toast.error(err.detail || 'Failed to save stream settings');
                    }
                  } catch (err) {
                    toast.error('Failed to save stream settings');
                  }
                }}
                className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold"
              >
                💾 Save Stream Settings
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* VOD URL Dialog */}
        <Dialog open={showVodDialog} onOpenChange={setShowVodDialog}>
          <DialogContent className="bg-rooam-gray-medium border-rooam-gray-light text-white max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">Set VOD URL</DialogTitle>
              <DialogDescription className="text-zinc-400">
                {vodForm.game_title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Video URL (Playback)</Label>
                <Input
                  value={vodForm.video_url}
                  onChange={(e) => setVodForm({...vodForm, video_url: e.target.value})}
                  placeholder="https://customer-xxx.cloudflarestream.com/VIDEO_UID/manifest/video.m3u8"
                  className="bg-rooam-black border-rooam-gray-light text-white"
                />
                <p className="text-xs text-zinc-500">
                  For Cloudflare: https://customer-xxx.cloudflarestream.com/VIDEO_UID/manifest/video.m3u8
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Cloudflare Video UID (optional)</Label>
                <Input
                  value={vodForm.cloudflare_video_uid}
                  onChange={(e) => setVodForm({...vodForm, cloudflare_video_uid: e.target.value})}
                  placeholder="ca4648ed9061f8fd02cdc13a5a165c4b"
                  className="bg-rooam-black border-rooam-gray-light text-white"
                />
                <p className="text-xs text-zinc-500">
                  The video UID from Cloudflare Stream (not the live input UID)
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Thumbnail URL (optional)</Label>
                <Input
                  value={vodForm.thumbnail_url}
                  onChange={(e) => setVodForm({...vodForm, thumbnail_url: e.target.value})}
                  placeholder="https://..."
                  className="bg-rooam-black border-rooam-gray-light text-white"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Duration (seconds, optional)</Label>
                <Input
                  type="number"
                  value={vodForm.duration_seconds}
                  onChange={(e) => setVodForm({...vodForm, duration_seconds: e.target.value})}
                  placeholder="3600"
                  className="bg-rooam-black border-rooam-gray-light text-white"
                />
              </div>

              {vodForm.video_url && (
                <div className="p-3 bg-zinc-800 rounded-lg">
                  <p className="text-xs text-zinc-400 mb-2">Preview URL:</p>
                  <a 
                    href={vodForm.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-cyan-400 hover:underline break-all"
                  >
                    {vodForm.video_url}
                  </a>
                </div>
              )}

              <Button
                onClick={async () => {
                  if (!vodForm.video_url) {
                    toast.error('Please enter a video URL');
                    return;
                  }
                  setSavingVod(true);
                  try {
                    const authToken = token || localStorage.getItem('rooam_token');
                    const res = await fetch(`${API_URL}/api/engine/games/${vodForm.game_id}/set-vod`, {
                      method: 'PUT',
                      headers: {
                        'Authorization': `Bearer ${authToken}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        video_url: vodForm.video_url,
                        cloudflare_video_uid: vodForm.cloudflare_video_uid || null,
                        thumbnail_url: vodForm.thumbnail_url || null,
                        duration_seconds: vodForm.duration_seconds ? parseFloat(vodForm.duration_seconds) : null
                      })
                    });
                    
                    if (res.ok) {
                      const result = await res.json();
                      toast.success('VOD URL saved successfully!');
                      // Update local state
                      setEngineGames(engineGames.map(g => 
                        g.game_id === vodForm.game_id 
                          ? {...g, video_url: vodForm.video_url, thumbnail_url: vodForm.thumbnail_url}
                          : g
                      ));
                      setShowVodDialog(false);
                    } else {
                      const errorText = await res.text();
                      console.error('VOD save failed:', res.status, errorText);
                      try {
                        const error = JSON.parse(errorText);
                        toast.error(error.detail || error.message || `Failed to save VOD URL (${res.status})`);
                      } catch {
                        toast.error(`Failed to save VOD URL: ${res.status} ${errorText.substring(0, 100)}`);
                      }
                    }
                  } catch (err) {
                    console.error('VOD save error:', err);
                    toast.error(`Failed to save VOD URL: ${err.message}`);
                  } finally {
                    setSavingVod(false);
                  }
                }}
                disabled={savingVod || !vodForm.video_url}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold"
              >
                {savingVod ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <>🎬 Save VOD URL</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

/* ========== PROGRAMS MANAGER (Super Admin) ========== */

export default AdminDashboard;
